import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import HistoryDialog from '@/components/HistoryDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/ui/page-header';
import { useBreadcrumbs } from '@/components/ui/breadcrumbs';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber, formatTimeAgo, toDateInputValue } from '@/lib/format';
import { canEdit as canEditModule, hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  ChevronDown,
  FlaskConical,
  GitMerge,
  History,
  Lock,
  Package,
  PackagePlus,
  RefreshCw,
  RotateCcw,
  Scale,
  Truck,
  Unlock
} from 'lucide-react';
import StockTab from '@/components/inventory/StockTab';
import SitesBoqTab from '@/components/inventory/SitesBoqTab';
import CatalogTab, { CATALOG_HEALTH } from '@/components/inventory/CatalogTab';
import MovementsTab from '@/components/inventory/MovementsTab';
import DashboardTab from '@/components/inventory/DashboardTab';
import RecordMovementPanel from '@/components/inventory/RecordMovementPanel';
import AddItemsDialog from '@/components/inventory/AddItemsDialog';
import CatalogItemDrawer from '@/components/inventory/CatalogItemDrawer';
import { useQbStock } from '@/components/inventory/QuickBooksStock';
import { DeliveryPlanEditor, DeliveryPlansPanel } from '@/components/inventory/DeliveryPlans';
import { documentFacts } from '@/components/inventory/MovementDocumentPanel';
import ReceiveDnSheet from '@/components/inventory/ReceiveDnSheet';
import { printStockDocument, printStockDocumentWhenReady } from '@/components/inventory/printDocument';
import {
  INVENTORY_LOCATIONS,
  MOVEMENT_PERIODS,
  boqDemandByPart,
  documentLabel,
  duplicateGroupsByKey,
  duplicateReviewGroups,
  formatQty,
  groupMovements,
  newDocumentId,
  onHandOf,
  parsePastedRows,
  partKey,
  plural,
  projectBookedQty,
  reservedOf,
  siteDeliveryNoteOf,
  siteDnReceipt,
  stockMovedParts,
  stockStatus,
  toQty
} from '@/components/inventory/inventoryModel';
import { downloadCsv, downloadXlsx } from '@/components/inventory/exportRows';

const ADMIN_LIKE_ROLES = ['Admin', 'Super Admin'];
// Delete / merge catalog items, reverse documents: Admin, Super Admin and Storekeeper (mirrors
// backend can_manage_inventory / require_inventory_delete).
const INVENTORY_DELETE_ROLES = [...ADMIN_LIKE_ROLES, 'Storekeeper'];

const MOVEMENT_TYPES = {
  GRN: {
    label: 'Goods Receipt',
    shortLabel: 'Goods Receipt',
    stepLabel: 'GRN — received',
    hint: 'Supplier → store',
    menuLabel: 'GRN — goods receipt',
    icon: Package,
    needsDestination: true,
    needsReference: true,
    defaultDestination: 'Abu Dhabi Store'
  },
  transfer: {
    label: 'Transfer',
    shortLabel: 'Transfer',
    stepLabel: 'Transfer',
    hint: 'Store → store',
    menuLabel: 'Transfer between stores',
    icon: ArrowRightLeft,
    needsSource: true,
    needsDestination: true,
    defaultSource: 'Abu Dhabi Store',
    defaultDestination: 'Dubai Store'
  },
  issue: {
    label: 'Site Delivery Note',
    shortLabel: 'Site Delivery Note',
    stepLabel: 'Site DN',
    hint: 'Store → project site',
    menuLabel: 'Site DN — issue to site',
    icon: Truck,
    needsSource: true,
    needsProject: true,
    needsReference: true,
    deliveryType: 'to_site',
    defaultSource: 'Dubai Store',
    defaultDestination: 'Site'
  },
  testing: {
    label: 'Lab Testing Use',
    shortLabel: 'Lab Testing Use',
    stepLabel: 'Lab use',
    hint: 'Store → Lab',
    menuLabel: 'Lab use',
    icon: FlaskConical,
    needsSource: true,
    defaultSource: 'Dubai Store',
    fixedDestination: 'Dubai Office Lab'
  },
  return: {
    label: 'Return to Stock',
    shortLabel: 'Return to Stock',
    stepLabel: 'Return',
    hint: 'Site → store',
    menuLabel: 'Return to stock',
    icon: RotateCcw,
    needsDestination: true,
    defaultDestination: 'Dubai Store'
  },
  adjustment: {
    label: 'Adjustment',
    shortLabel: 'Adjustment',
    stepLabel: 'Adjustment',
    hint: '+ / − with a reason',
    menuLabel: 'Adjustment',
    icon: Scale,
    needsSource: true,
    defaultSource: 'Abu Dhabi Store',
    defaultDestination: 'Abu Dhabi Store'
  }
};

const createMovementRow = (partNumber = '', qty = 0) => ({ part_number: partNumber, qty });

const createStockEditDraft = (row) => ({
  part_number: row?.part_number || '',
  description: row?.description || '',
  brand: row?.brand || '',
  category: row?.category || '',
  on_hand_qty: row?.on_hand_qty ?? 0
});

const createMovementForm = () => ({
  part_number: '',
  movement_type: 'GRN',
  qty: 0,
  reference: '',
  project_id: '',
  boq_item_id: '',
  source_location: 'Abu Dhabi Store',
  destination_location: 'Abu Dhabi Store',
  notes: '',
  delivery_type: 'to_stock'
});

const createReserveForm = () => ({
  project_id: '',
  part_number: '',
  qty_reserved: 0,
  needed_by_date: '',
  location: 'Abu Dhabi Store',
  boq_item_id: ''
});

const normalizePartNumber = (value) => String(value || '').trim().toUpperCase();

const fieldClass = 'rounded-control border-line bg-surface text-ink';
const labelClass = 'text-[12px] font-semibold text-ink-3';

const INVENTORY_TABS = ['dashboard', 'stock', 'sites-boq', 'catalog', 'movements'];
const INVENTORY_TAB_LABELS = { dashboard: 'Dashboard', stock: 'Stock', 'sites-boq': 'Sites BOQ', catalog: 'Catalog', movements: 'Movements' };
// ?filter=no-min-level (Dashboard "Data to fix" → Set levels) = the Catalog's "No min level" health chip.
const catalogHealthFromParam = (value) => {
  const key = String(value || '').trim().toLowerCase().replace(/-/g, '_');
  return CATALOG_HEALTH.some((entry) => entry.key === key) ? key : null;
};
const userKey = (user) => String(user?.id || user?.sub || '').trim();
/** GET /stock/movements without a period returns this many lines (the server default); a period asks for up to MOVEMENTS_PERIOD_LIMIT. */
const MOVEMENTS_DEFAULT_LIMIT = 2000;
const MOVEMENTS_PERIOD_LIMIT = 10000;
/** A scheduled hourly job has run (for real, not a dry run) within this long. */
const JOB_FRESH_MS = 3 * 3600000;

export default function InventoryPage() {
  const { user } = useAuth();
  // The open tab lives in the URL (?tab=catalog); links can also pass ?filter= (Catalog health) and ?search=.
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const isAdminLike = ADMIN_LIKE_ROLES.includes(user?.role);
  const canDeleteItems = INVENTORY_DELETE_ROLES.includes(user?.role);
  const canManageStock = canDeleteItems;
  // Viewer accounts — and roles whose Inventory level is View in Admin › Access (view_only_modules, wave 3 ·
  // 28 #8) — are read-only on the server (auth_utils / edit_level_guard reject every write): the page offers
  // them no write action at all — no Record movement, Reserve, Issue DN, Release, Adjust, plans, attach…
  const canEdit = canEditModule(user, 'inventory');
  const canEditCatalog = canEdit;
  const canLinkPo = canEdit && hasPermission(user, 'procurement');
  // Purchase requests are Material (Procurement › Site requests): a View-only Material level cannot send them.
  const canRequestPurchase = canEdit && canEditModule(user, 'material');
  // Auto-book stock (Sites BOQ 19 #7) books for many projects at once: Admin, Super Admin, Storekeeper (server-enforced).
  const canAutoBook = canManageStock;

  const [catalog, setCatalog] = useState([]);
  const [stock, setStock] = useState([]);
  const [sitesBoq, setSitesBoq] = useState([]);
  const [siteRequests, setSiteRequests] = useState([]);
  const [movements, setMovements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [reservationsState, setReservationsState] = useState({ loading: true, error: '' });
  const [deliveryPlans, setDeliveryPlans] = useState([]);
  const [plansState, setPlansState] = useState({ loading: true, error: '' });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [, setClock] = useState(0);
  const [search, setSearch] = useState(() => String(searchParams.get('search') || '').trim());
  const [debouncedSearch, setDebouncedSearch] = useState(() => String(searchParams.get('search') || '').trim());
  const [brandFilter, setBrandFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [catalogDialog, setCatalogDialog] = useState(false);
  const [movementDialog, setMovementDialog] = useState(false);
  const [movementSubmitting, setMovementSubmitting] = useState(false);
  const [reserveDialog, setReserveDialog] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
  const [selectedCatalogItemsById, setSelectedCatalogItemsById] = useState({});
  const [selectedStockPartNumbers, setSelectedStockPartNumbers] = useState([]);
  // One stock row is edited at a time — independent of the row selection.
  const [stockEdit, setStockEdit] = useState(null); // { partNumber, draft }
  const [stockExpanded, setStockExpanded] = useState(null);
  const [movementForm, setMovementForm] = useState(createMovementForm());
  const [movementRows, setMovementRows] = useState([createMovementRow()]);
  const [deliveryPreview, setDeliveryPreview] = useState(null);
  const [reserveForm, setReserveForm] = useState(createReserveForm());
  const [reserveConfirming, setReserveConfirming] = useState(false);
  const [reserveBoqItems, setReserveBoqItems] = useState([]);
  const [movementProjectBoqItems, setMovementProjectBoqItems] = useState([]);
  const [movementProjectBoqStatus, setMovementProjectBoqStatus] = useState('idle');
  // The project the loaded BOQ belongs to — a BOQ is never compared with another project's items.
  const [movementBoqProjectId, setMovementBoqProjectId] = useState('');
  // Pre-filled items taken off because the picked project's BOQ does not list them (after asking).
  const [movementDroppedParts, setMovementDroppedParts] = useState([]);
  // Items the newly picked project's BOQ does not list: asked about before they are taken off.
  const [pendingDrop, setPendingDrop] = useState(null);
  const previousProjectRef = useRef('');
  // A delivery plan being converted into this Site DN (closed with the DN's reference afterwards).
  const [convertingPlan, setConvertingPlan] = useState(null);
  const [plansPanelOpen, setPlansPanelOpen] = useState(false);
  const [planEditor, setPlanEditor] = useState({ open: false, plan: null });
  const [catalogDrawerItem, setCatalogDrawerItem] = useState(null);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [movementDocKey, setMovementDocKey] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const requested = String(searchParams.get('tab') || '').trim().toLowerCase();
    if (INVENTORY_TABS.includes(requested)) return requested;
    return catalogHealthFromParam(searchParams.get('filter')) ? 'catalog' : 'dashboard';
  });
  const [stockQuickFilter, setStockQuickFilter] = useState('has');
  const [stockLocationFilter, setStockLocationFilter] = useState(null);
  const [stockProjectFilter, setStockProjectFilter] = useState(null);
  const [stockNeedsFix, setStockNeedsFix] = useState(false);
  const [stockQbDiffers, setStockQbDiffers] = useState(false);
  // QuickBooks Desktop (read only): asked once per visit; null while off, not read yet or unreachable.
  const qbStock = useQbStock();
  const [sitesBoqStatus, setSitesBoqStatus] = useState('all');
  const [sitesBoqProject, setSitesBoqProject] = useState(null);
  // The Site DN being confirmed on site (21 #9 / 17 #8) — opened from the Dashboard or a DN's panel.
  const [receiveDn, setReceiveDn] = useState(null);
  // ?dn=<delivery note id> (a reminder / "DN received" notification) opens that DN once the ledger has
  // loaded — on a fresh load AND when the bell navigates here while the page is open (see the effect below).
  const linkedDnId = String(searchParams.get('dn') || '').trim();
  const mountDnRef = useRef(linkedDnId);
  const [movementTypeFilter, setMovementTypeFilter] = useState('all');
  // GET /stock/movements returns the newest 2,000 lines: `movementsCapped` says the ledger may be cut off;
  // a Movements period reaching further back reloads that period from the server (date_from).
  const [movementsCapped, setMovementsCapped] = useState(false);
  const movementsSinceRef = useRef(null);
  // 48 h Site DN reminders (17 #8): is the hourly job scheduled? Admins only (GET /jobs) — null = unknown.
  const [dnReminders, setDnReminders] = useState(null);
  // The page belongs to one set of filters: a filter change shows page 1 in the same render, so
  // the catalog is requested once (not with the old page first).
  const [catalogPaging, setCatalogPaging] = useState({ key: null, page: 1 });
  const [catalogLimit, setCatalogLimit] = useState(50);
  const [catalogTotalPages, setCatalogTotalPages] = useState(1);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSupplierFilter, setCatalogSupplierFilter] = useState(null);
  const [catalogHealthFilter, setCatalogHealthFilter] = useState(() => catalogHealthFromParam(searchParams.get('filter')));
  useBreadcrumbs([INVENTORY_TAB_LABELS[activeTab]]);
  const tabsBarRef = useRef(null);
  const tabsListRef = useRef(null);
  // Phones: the tab strip scrolls sideways — keep the open tab in view (on load and on every change).
  useEffect(() => {
    const list = tabsListRef.current;
    const trigger = list?.querySelector?.(`[data-testid="tab-${activeTab}"]`);
    if (!list || !trigger || list.scrollWidth <= list.clientWidth) return;
    const offset = trigger.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
    list.scrollLeft = Math.max(0, offset - (list.clientWidth - trigger.offsetWidth) / 2);
  }, [activeTab]);
  // Keep the URL in step: ?tab= follows the open tab; a linked ?filter= goes once that chip is cleared.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeTab === 'dashboard') next.delete('tab'); else next.set('tab', activeTab);
    if (next.has('filter') && catalogHealthFromParam(next.get('filter')) !== catalogHealthFilter) next.delete('filter');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [activeTab, catalogHealthFilter, searchParams, setSearchParams]);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeForm, setMergeForm] = useState({ survivor_item_id: '', final_part_number: '', final_description: '' });

  /** Bookings and delivery plans load on their own: a failure there never blanks the stock. */
  const fetchReservations = useCallback(async () => {
    setReservationsState((current) => ({ ...current, loading: true }));
    try {
      const res = await api.get('/reservations', { params: { status: 'Active' } });
      setReservations(Array.isArray(res.data) ? res.data : []);
      setReservationsState({ loading: false, error: '' });
    } catch (err) {
      setReservationsState({ loading: false, error: getErrorMessage(err, 'Could not load the bookings') });
    }
  }, []);

  const fetchDeliveryPlans = useCallback(async () => {
    setPlansState((current) => ({ ...current, loading: true }));
    try {
      const res = await api.get('/delivery-plans');
      setDeliveryPlans(Array.isArray(res.data) ? res.data : []);
      setPlansState({ loading: false, error: '' });
    } catch (err) {
      setPlansState({ loading: false, error: getErrorMessage(err, 'Could not load delivery plans') });
    }
  }, []);

  /** Catalog rows whose part number appears more than once (whole catalog) → "Duplicate ×2" badges. */
  const fetchDuplicateItems = useCallback(async () => {
    try {
      const res = await api.get('/catalog', { params: { health: 'duplicates', limit: 200, page: 1 } });
      setDuplicateItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      setDuplicateItems([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    fetchReservations();
    fetchDeliveryPlans();
    const movementsSince = movementsSinceRef.current;
    try {
      const [sRes, sbRes, srRes, mRes, pRes] = await Promise.all([
        api.get('/stock'),
        api.get('/sites-boq'),
        api.get('/site-requests'),
        movementsSince
          ? api.get('/stock/movements', { params: { date_from: movementsSince, limit: MOVEMENTS_PERIOD_LIMIT } })
          : api.get('/stock/movements'),
        api.get('/projects'),
      ]);
      setStock(sRes.data || []);
      setSitesBoq(sbRes.data || []);
      setSiteRequests(srRes.data || []);
      setMovements(mRes.data || []);
      setMovementsCapped(!movementsSince && (mRes.data || []).length >= MOVEMENTS_DEFAULT_LIMIT);
      setProjects(pRes.data || []);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      notify.error('Could not load inventory', { description: getErrorMessage(err) });
    }
    setLoading(false);
  }, [fetchDeliveryPlans, fetchReservations]);

  const catalogFilterKey = JSON.stringify([debouncedSearch, brandFilter, categoryFilter, catalogSupplierFilter, catalogHealthFilter, catalogLimit]);
  // The page belongs to the newest filter set only: when the filters change, the remembered page is reset
  // (in this render), so toggling a filter off again returns to page 1 rather than to the old page 3.
  if (catalogPaging.key !== catalogFilterKey) setCatalogPaging({ key: catalogFilterKey, page: 1 });
  const catalogPage = catalogPaging.key === catalogFilterKey ? catalogPaging.page : 1;
  const setCatalogPage = useCallback((page) => setCatalogPaging({ key: catalogFilterKey, page }), [catalogFilterKey]);

  const catalogParams = useMemo(() => ({
    page: catalogPage,
    limit: catalogLimit,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(brandFilter ? { brand: brandFilter } : {}),
    ...(categoryFilter ? { category: categoryFilter } : {}),
    ...(catalogSupplierFilter ? { supplier: catalogSupplierFilter } : {}),
    ...(catalogHealthFilter ? { health: catalogHealthFilter } : {})
  }), [brandFilter, catalogHealthFilter, catalogLimit, catalogPage, catalogSupplierFilter, categoryFilter, debouncedSearch]);
  const catalogParamsKey = JSON.stringify(catalogParams);

  // Whole-catalog facets + health counts (a full scan on the server) are asked for on the first load
  // and after a change to the catalog — not on every page / filter request.
  const catalogMetaStaleRef = useRef(true);
  const catalogFetchedKeyRef = useRef(null);
  // Filters can change faster than the server answers — only the newest catalog request may land.
  const catalogRequestRef = useRef(0);
  const fetchCatalog = useCallback(async ({ withMeta } = {}) => {
    if (withMeta) catalogMetaStaleRef.current = true;
    const includeMeta = catalogMetaStaleRef.current;
    const requestId = catalogRequestRef.current + 1;
    catalogRequestRef.current = requestId;
    catalogFetchedKeyRef.current = catalogParamsKey;
    setCatalogLoading(true);
    if (includeMeta) fetchDuplicateItems();
    try {
      const cRes = await api.get('/catalog', { params: includeMeta ? { ...catalogParams, include_meta: true } : catalogParams });
      if (requestId !== catalogRequestRef.current) return;
      const data = cRes.data || {};
      const items = data.items || (Array.isArray(data) ? data : []);
      setCatalog(items);
      setCatalogTotalPages(data.total_pages || 1);
      setCatalogTotal(data.total ?? items.length);
      if (data.health || data.facets) {
        setCatalogMeta({ health: data.health, facets: data.facets, catalog_total: data.catalog_total, suggestions: data.suggestions || null });
        catalogMetaStaleRef.current = false;
      }
    } catch (err) {
      if (requestId === catalogRequestRef.current) {
        notify.error('Could not load the catalog', { description: getErrorMessage(err) });
      }
    } finally {
      if (requestId === catalogRequestRef.current) setCatalogLoading(false);
    }
  }, [catalogParams, catalogParamsKey, fetchDuplicateItems]);
  /** After the catalog changed (add, edit, merge, delete, undo): reload the page and its counts. */
  const refreshCatalog = useCallback(() => fetchCatalog({ withMeta: true }), [fetchCatalog]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchData(), refreshCatalog()]);
    setRefreshing(false);
  }, [fetchData, refreshCatalog]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Admins see whether the 48 h Site DN reminder runs: the hourly job counts as scheduled once it has really
  // run (not a dry run) in the last few hours. Anyone else gets null and the card shows nothing about it.
  useEffect(() => {
    if (!isAdminLike) return undefined;
    let ignore = false;
    api.get('/jobs').then((res) => {
      if (ignore) return;
      const job = (res.data?.jobs || []).find((entry) => entry.name === 'inventory_dn_unconfirmed');
      if (!job) return;
      const last = job.last_run || null;
      const ranAt = last && !last.dry_run && last.status === 'succeeded' ? Date.parse(last.started_at) : NaN;
      setDnReminders({ scheduled: Number.isFinite(ranAt) && Date.now() - ranAt <= JOB_FRESH_MS, lastRun: last });
    }).catch(() => {});
    return () => { ignore = true; };
  }, [isAdminLike]);
  // The search box and Brand / Category are shared with Stock, Sites BOQ and Movements: while the
  // Catalog tab is hidden its list waits, and loads once when the tab opens with new filters.
  useEffect(() => {
    if (catalogFetchedKeyRef.current !== null && activeTab !== 'catalog') return;
    if (catalogFetchedKeyRef.current === catalogParamsKey) return;
    fetchCatalog();
  }, [activeTab, catalogParamsKey, fetchCatalog]);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const timer = setInterval(() => setClock((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    let ignore = false;
    const loadProjectBoq = async () => {
      if (!reserveForm.project_id) {
        setReserveBoqItems([]);
        return;
      }
      try {
        const res = await api.get('/materials/boq-items', { params: { project_id: reserveForm.project_id } });
        if (!ignore) setReserveBoqItems(res.data || []);
      } catch (err) {
        if (!ignore) setReserveBoqItems([]);
      }
    };
    loadProjectBoq();
    return () => { ignore = true; };
  }, [reserveForm.project_id]);
  useEffect(() => {
    let ignore = false;
    const loadMovementProjectBoq = async () => {
      if (movementForm.movement_type !== 'issue' || !movementForm.project_id) {
        setMovementProjectBoqItems([]);
        setMovementProjectBoqStatus('idle');
        return;
      }
      setMovementProjectBoqItems([]);
      setMovementProjectBoqStatus('loading');
      try {
        const res = await api.get('/materials/boq-items', { params: { project_id: movementForm.project_id } });
        if (ignore) return;
        setMovementProjectBoqItems(res.data || []);
        setMovementBoqProjectId(movementForm.project_id);
        setMovementProjectBoqStatus((res.data || []).length > 0 ? 'loaded' : 'empty');
      } catch (err) {
        if (ignore) return;
        setMovementProjectBoqItems([]);
        setMovementProjectBoqStatus('error');
      }
    };
    loadMovementProjectBoq();
    return () => { ignore = true; };
  }, [movementForm.movement_type, movementForm.project_id]);

  const selectedMovementConfig = MOVEMENT_TYPES[movementForm.movement_type] || MOVEMENT_TYPES.GRN;
  const stockByPartNumber = useMemo(() => (
    new Map(stock.map((item) => [item.part_number, item]))
  ), [stock]);
  // The store holding the most stock: Record movement starts there when no item is chosen yet, so a Site DN
  // does not open on an empty store (today everything is in Abu Dhabi Store, the type default is Dubai Store).
  const busiestStore = useMemo(() => INVENTORY_LOCATIONS
    .map((location) => ({ location, qty: stock.reduce((sum, item) => sum + Math.max(0, toQty(item.location_quantities?.[location])), 0) }))
    .filter((entry) => entry.qty > 0)
    .sort((left, right) => right.qty - left.qty)[0]?.location || '', [stock]);

  const inventoryItemOptions = useMemo(() => {
    const itemsByPartNumber = new Map();
    [...stock, ...catalog].forEach((item) => {
      const partNumber = String(item.part_number || '').trim();
      if (!partNumber) return;
      itemsByPartNumber.set(partNumber, {
        ...itemsByPartNumber.get(partNumber),
        ...item,
        part_number: partNumber
      });
    });
    return [...itemsByPartNumber.values()].sort((a, b) => a.part_number.localeCompare(b.part_number));
  }, [catalog, stock]);

  const itemDescriptions = useMemo(() => (
    Object.fromEntries(inventoryItemOptions.map((item) => [item.part_number, item.description || '']))
  ), [inventoryItemOptions]);

  const projectById = useMemo(() => (
    new Map(projects.map((project) => [project.id, project]))
  ), [projects]);

  const getAvailableQty = useCallback((partNumber, location = '') => {
    const item = stockByPartNumber.get(partNumber);
    if (!item) return 0;
    if (location) {
      if (item.available_by_location?.[location] !== undefined) {
        return Math.max(0, Number(item.available_by_location[location] || 0));
      }
      const locationQty = Number(item.location_quantities?.[location] ?? item.on_hand_qty ?? 0);
      const reservedAtLocation = Number(item.reserved_by_location?.[location] || 0);
      return Math.max(0, locationQty - reservedAtLocation);
    }
    return Math.max(0, Number(item.available_qty ?? (Number(item.on_hand_qty || 0) - Number(item.reserved_qty || 0))));
  }, [stockByPartNumber]);

  const getCatalogLabel = useCallback((partNumber, location = '') => {
    const item = inventoryItemOptions.find((entry) => entry.part_number === partNumber) || stockByPartNumber.get(partNumber);
    const availableQty = getAvailableQty(partNumber, location);
    if (!item) return '';
    return `${item.part_number} — ${item.description || 'No description'} (${availableQty} available)`;
  }, [getAvailableQty, inventoryItemOptions, stockByPartNumber]);

  const resetMovementModal = useCallback((movementType = 'GRN', lines = []) => {
    const config = MOVEMENT_TYPES[movementType] || MOVEMENT_TYPES.GRN;
    setMovementForm({
      ...createMovementForm(),
      movement_type: movementType,
      delivery_type: config.deliveryType || 'to_stock',
      source_location: config.defaultSource || 'Abu Dhabi Store',
      destination_location: config.fixedDestination || config.defaultDestination || 'Abu Dhabi Store'
    });
    setMovementRows(lines.length
      ? lines.map((line) => (typeof line === 'string' ? createMovementRow(line) : createMovementRow(line.part_number, line.qty || 0)))
      : [createMovementRow()]);
    setMovementProjectBoqItems([]);
    setMovementProjectBoqStatus('idle');
    setMovementDroppedParts([]);
    setPendingDrop(null);
    previousProjectRef.current = '';
    setDeliveryPreview(null);
  }, []);

  /**
   * Opens Record movement; items arrive pre-filled (Stock selection, a booking, a delivery plan…).
   * `lines` = part numbers or { part_number, qty }; `options` = { projectId, sourceLocation, reference, notes, plan }.
   */
  const openMovementDialog = useCallback((movementType = 'GRN', lines = [], options = {}) => {
    const config = MOVEMENT_TYPES[movementType] || MOVEMENT_TYPES.GRN;
    const partNumbers = lines.map((line) => (typeof line === 'string' ? line : line.part_number)).filter(Boolean);
    // A transfer / issue starts from the store that actually holds the first selected item.
    const firstItem = partNumbers.length ? stockByPartNumber.get(partNumbers[0]) : null;
    resetMovementModal(movementType, lines);
    const startStore = options.sourceLocation || (!firstItem && movementType !== 'adjustment' ? busiestStore : '');
    if (startStore && config.needsSource) {
      setMovementForm((prev) => ({
        ...prev,
        source_location: startStore,
        destination_location: movementType === 'adjustment'
          ? startStore
          : movementType === 'transfer' && prev.destination_location === startStore
            ? INVENTORY_LOCATIONS.find((location) => location !== startStore) || prev.destination_location
            : prev.destination_location
      }));
    } else if (firstItem && config.needsSource) {
      const holding = INVENTORY_LOCATIONS.find((location) => Number(firstItem.location_quantities?.[location] || 0) > 0);
      if (holding) {
        setMovementForm((prev) => ({
          ...prev,
          source_location: holding,
          destination_location: movementType === 'transfer' && prev.destination_location === holding
            ? INVENTORY_LOCATIONS.find((location) => location !== holding) || prev.destination_location
            : (movementType === 'adjustment' ? holding : prev.destination_location)
        }));
      }
    }
    if (options.projectId && config.needsProject) {
      previousProjectRef.current = '';
      setMovementForm((prev) => ({ ...prev, project_id: options.projectId }));
    }
    if (options.reference || options.notes) {
      setMovementForm((prev) => ({ ...prev, reference: options.reference || prev.reference, notes: options.notes || prev.notes }));
    }
    setConvertingPlan(options.plan || null);
    setMovementDialog(true);
  }, [busiestStore, resetMovementModal, stockByPartNumber]);

  const closeMovementDialog = useCallback(() => {
    setMovementDialog(false);
    setConvertingPlan(null);
    resetMovementModal('GRN');
  }, [resetMovementModal]);

  const resetReserveModal = useCallback(() => {
    setReserveForm(createReserveForm());
    setReserveBoqItems([]);
    setReserveConfirming(false);
  }, []);

  /** Opens Reserve; `options` = { projectId, boqItemId, qty, location } pre-fill it (Sites BOQ, reorder list). */
  const openReserveDialog = useCallback((partNumbers = [], options = {}) => {
    resetReserveModal();
    const firstPart = Array.isArray(partNumbers) ? partNumbers[0] : '';
    if (firstPart) {
      const item = stockByPartNumber.get(firstPart);
      const holding = INVENTORY_LOCATIONS
        .map((location) => ({ location, qty: Number(item?.available_by_location?.[location] ?? item?.location_quantities?.[location] ?? 0) }))
        .sort((left, right) => right.qty - left.qty)
        .find((entry) => entry.qty > 0)?.location;
      setReserveForm((prev) => ({
        ...prev,
        part_number: firstPart,
        location: options.location || holding || prev.location,
        project_id: options.projectId || prev.project_id,
        boq_item_id: options.boqItemId || prev.boq_item_id,
        qty_reserved: options.qty ? Number(options.qty) : prev.qty_reserved
      }));
    }
    setReserveDialog(true);
  }, [resetReserveModal, stockByPartNumber]);

  const handleMovementTypeChange = (movementType) => {
    const config = MOVEMENT_TYPES[movementType] || MOVEMENT_TYPES.GRN;
    // Switching to a type that takes stock out starts from a store that has it: the first item's store, else the
    // store holding the most stock — not the type's fixed default (which may be empty).
    const firstPart = movementRows.map((row) => row.part_number).find(Boolean);
    const firstItem = firstPart ? stockByPartNumber.get(firstPart) : null;
    const holding = firstItem ? INVENTORY_LOCATIONS.find((location) => Number(firstItem.location_quantities?.[location] || 0) > 0) : '';
    const startStore = config.needsSource && movementType !== 'adjustment' ? (holding || busiestStore) : '';
    setMovementForm((prev) => {
      const source = startStore || config.defaultSource || prev.source_location || 'Abu Dhabi Store';
      const destination = config.fixedDestination || config.defaultDestination || prev.destination_location || 'Abu Dhabi Store';
      return {
        ...prev,
        movement_type: movementType,
        delivery_type: config.deliveryType || 'to_stock',
        source_location: source,
        destination_location: movementType === 'transfer' && destination === source
          ? INVENTORY_LOCATIONS.find((location) => location !== source) || destination
          : destination,
        project_id: config.needsProject ? prev.project_id : '',
        boq_item_id: config.needsProject ? prev.boq_item_id : '',
        reference: config.needsReference || movementType === 'adjustment' ? prev.reference : ''
      };
    });
    setPendingDrop(null);
    setDeliveryPreview(null);
  };

  const handleMovementProjectChange = (value) => {
    if (value === movementForm.project_id) return;
    // While a "not in this BOQ" question is open, the project to go back to stays the one before it.
    previousProjectRef.current = pendingDrop ? (pendingDrop.previousId || '') : (movementForm.project_id || '');
    setMovementProjectBoqItems([]);
    setMovementProjectBoqStatus('loading');
    setMovementDroppedParts([]);
    setPendingDrop(null);
    setMovementForm((prev) => ({ ...prev, project_id: value, boq_item_id: '' }));
  };

  const reserveProjectBoqOptions = useMemo(() => (
    reserveBoqItems.filter((item) => !reserveForm.part_number || item.part_number === reserveForm.part_number)
  ), [reserveBoqItems, reserveForm.part_number]);

  const reserveAvailableQty = useMemo(() => (
    getAvailableQty(reserveForm.part_number, reserveForm.location)
  ), [getAvailableQty, reserveForm.location, reserveForm.part_number]);

  const reserveQty = Number(reserveForm.qty_reserved || 0);
  const reserveAfterQty = reserveAvailableQty - reserveQty;
  const reserveHasStockError = Boolean(reserveForm.part_number && reserveQty > reserveAvailableQty);
  const reserveSelectedProject = projectById.get(reserveForm.project_id);
  const reserveSelectedItem = stockByPartNumber.get(reserveForm.part_number)
    || inventoryItemOptions.find((item) => item.part_number === reserveForm.part_number);

  const handleReserveFormChange = useCallback((updates) => {
    setReserveForm((prev) => ({ ...prev, ...updates }));
    setReserveConfirming(false);
  }, []);

  const movementUsesSourceStock = ['transfer', 'issue', 'testing'].includes(movementForm.movement_type);
  // Part numbers (normalised) the picked project's BOQ lists — null until the BOQ has loaded.
  const movementBoqPartKeys = useMemo(() => {
    if (movementForm.movement_type !== 'issue' || !movementForm.project_id || movementProjectBoqStatus !== 'loaded') return null;
    if (movementBoqProjectId !== movementForm.project_id) return null;
    return new Set(movementProjectBoqItems
      .map((boqItem) => normalizePartNumber(boqItem.part_number))
      .filter((key) => key && key !== '-'));
  }, [movementBoqProjectId, movementForm.movement_type, movementForm.project_id, movementProjectBoqItems, movementProjectBoqStatus]);

  // Picking a project for a Site DN keeps the items already on it. Once the project's BOQ has loaded,
  // the items that BOQ does not list are named and the user decides: take them off, or keep the
  // previous project (Movements idea 6 — nothing is wiped without asking). An empty BOQ keeps
  // everything — the dialog then offers all items.
  const movementRowsRef = useRef(movementRows);
  movementRowsRef.current = movementRows;
  useEffect(() => {
    if (!movementBoqPartKeys) return;
    const rows = movementRowsRef.current;
    const dropped = rows.filter((row) => row.part_number && !movementBoqPartKeys.has(normalizePartNumber(row.part_number)));
    if (!dropped.length) return;
    const previousId = previousProjectRef.current;
    setPendingDrop({
      parts: dropped.map((row) => row.part_number),
      projectName: projectById.get(movementForm.project_id)?.name || 'this project',
      previousId,
      previousName: previousId ? projectById.get(previousId)?.name || '' : ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movementBoqPartKeys]);

  const confirmMovementDrop = () => {
    if (!pendingDrop) return;
    const dropKeys = new Set(pendingDrop.parts.map(normalizePartNumber));
    setMovementRows((rows) => {
      const kept = rows.filter((row) => !dropKeys.has(normalizePartNumber(row.part_number)));
      return kept.length ? kept : [createMovementRow()];
    });
    setMovementDroppedParts(pendingDrop.parts);
    setPendingDrop(null);
  };

  const keepPreviousMovementProject = () => {
    const previousId = pendingDrop?.previousId || '';
    setPendingDrop(null);
    previousProjectRef.current = '';
    setMovementProjectBoqItems([]);
    setMovementProjectBoqStatus(previousId ? 'loading' : 'idle');
    setMovementForm((prev) => ({ ...prev, project_id: previousId, boq_item_id: '' }));
  };

  const movementCatalogOptions = useMemo(() => {
    if (movementForm.movement_type === 'issue' && movementForm.project_id) {
      if (movementProjectBoqStatus === 'loaded') {
        const optionsByPartNumber = new Map();
        movementProjectBoqItems.forEach((boqItem) => {
          const normalizedPartNumber = normalizePartNumber(boqItem.part_number);
          if (!normalizedPartNumber || normalizedPartNumber === '-') return;
          const inventoryItem = inventoryItemOptions.find(
            (item) => normalizePartNumber(item.part_number) === normalizedPartNumber
          );
          if (!optionsByPartNumber.has(normalizedPartNumber)) {
            optionsByPartNumber.set(normalizedPartNumber, {
              ...boqItem,
              ...inventoryItem,
              part_number: String(inventoryItem?.part_number || boqItem.part_number || '').trim(),
              description: inventoryItem?.description || boqItem.description || ''
            });
          }
        });
        // Items still waiting for the "not in this BOQ" answer stay listed so their rows stay readable.
        if (pendingDrop) {
          pendingDrop.parts.forEach((partNumber) => {
            const key = normalizePartNumber(partNumber);
            const item = inventoryItemOptions.find((entry) => normalizePartNumber(entry.part_number) === key);
            if (item && !optionsByPartNumber.has(key)) optionsByPartNumber.set(key, item);
          });
        }
        return [...optionsByPartNumber.values()];
      }
      if (movementProjectBoqStatus === 'empty') return inventoryItemOptions;
      // While the BOQ loads (or failed), keep the rows' current items listed so they stay readable.
      const current = new Set(movementRows.map((row) => row.part_number).filter(Boolean));
      return inventoryItemOptions.filter((item) => current.has(item.part_number));
    }

    return inventoryItemOptions.filter((item) => (
      !movementUsesSourceStock || getAvailableQty(item.part_number, movementForm.source_location) > 0
      || movementRows.some((row) => row.part_number === item.part_number)
    ));
  }, [
    getAvailableQty,
    inventoryItemOptions,
    movementForm.movement_type,
    movementForm.project_id,
    movementForm.source_location,
    movementProjectBoqItems,
    movementProjectBoqStatus,
    movementRows,
    movementUsesSourceStock,
    pendingDrop
  ]);

  /**
   * What a movement can take from a store: { qty, booked }. Free stock for every movement; a Site DN may
   * also use its project's own active bookings at that store — the server issues them and fulfils the
   * booking (create_movement) — as far as those pieces are physically there.
   */
  const projectIssuable = useCallback((partNumber, projectId, location) => {
    const free = getAvailableQty(partNumber, location);
    const booked = projectId && location ? projectBookedQty(reservations, { partNumber, projectId, location }) : 0;
    if (booked <= 0) return { qty: free, booked: 0 };
    const onHandAt = Math.max(0, Number(stockByPartNumber.get(partNumber)?.location_quantities?.[location] ?? 0));
    const usable = Math.min(booked, Math.max(0, onHandAt - free));
    return { qty: free + usable, booked: usable };
  }, [getAvailableQty, reservations, stockByPartNumber]);
  const getMovementStock = useCallback((partNumber, location) => (
    movementForm.movement_type === 'issue' && movementForm.project_id
      ? projectIssuable(partNumber, movementForm.project_id, location)
      : { qty: getAvailableQty(partNumber, location), booked: 0 }
  ), [getAvailableQty, movementForm.movement_type, movementForm.project_id, projectIssuable]);

  /** Sites BOQ › Issue DN: start from the store that can give this project the most (its booking counts). */
  const openSiteDnForLine = useCallback((line, detail) => {
    const projectId = detail?.project_id || '';
    const best = INVENTORY_LOCATIONS
      .map((location) => ({ location, ...projectIssuable(line.part_number, projectId, location) }))
      .sort((left, right) => right.qty - left.qty)[0];
    const qty = Math.min(toQty(detail?.remaining_qty), best?.qty || 0);
    openMovementDialog('issue', [{ part_number: line.part_number, qty }], {
      projectId,
      sourceLocation: best?.qty > 0 ? best.location : undefined
    });
  }, [openMovementDialog, projectIssuable]);

  const getMovementItemLabel = useCallback((item) => (
    `${item.part_number} — ${item.description || 'No description'} (${movementUsesSourceStock
      ? getMovementStock(item.part_number, movementForm.source_location).qty
      : getAvailableQty(item.part_number, '')} available)`
  ), [getAvailableQty, getMovementStock, movementForm.source_location, movementUsesSourceStock]);

  /** Site DN: how much of what is issued comes out of the project's own bookings (shown under the items). */
  const movementBookingNote = useMemo(() => {
    if (movementForm.movement_type !== 'issue' || !movementForm.project_id) return '';
    const booked = movementRows
      .filter((row) => row.part_number)
      .reduce((sum, row) => sum + Math.min(Math.abs(Number(row.qty) || 0), getMovementStock(row.part_number, movementForm.source_location).booked), 0);
    if (booked <= 0) return '';
    const projectName = projectById.get(movementForm.project_id)?.name || 'this project';
    return `${formatQty(booked)} pcs come from stock booked for ${projectName} in ${movementForm.source_location} — recording the DN fulfils that booking.`;
  }, [getMovementStock, movementForm.movement_type, movementForm.project_id, movementForm.source_location, movementRows, projectById]);

  /** Step 2 of Record movement: what the chosen items have in a store (null when no item is chosen yet). */
  const movementSourceAvailability = useCallback((location) => {
    const parts = movementRows.map((row) => row.part_number).filter(Boolean);
    if (!parts.length) return null;
    return parts.reduce((sum, partNumber) => sum + getMovementStock(partNumber, location).qty, 0);
  }, [getMovementStock, movementRows]);

  // One "site demand" for the whole page (Dashboard reorder list, Stock column): the Sites BOQ still to
  // deliver that nobody has booked — the same lines Sites BOQ lists.
  const boqDemand = useMemo(() => boqDemandByPart(sitesBoq), [sitesBoq]);
  const siteDemandOf = useCallback((item) => toQty(boqDemand.get(item?.part_number)), [boqDemand]);

  const selectedCatalogItems = useMemo(
    () => selectedCatalogIds
      .map((itemId) => catalog.find((item) => item.id === itemId) || selectedCatalogItemsById[itemId])
      .filter(Boolean),
    [catalog, selectedCatalogIds, selectedCatalogItemsById]
  );

  const allCatalogSelected = catalog.length > 0 && catalog.every((item) => selectedCatalogIds.includes(item.id));
  const duplicateKeys = useMemo(() => duplicateGroupsByKey(duplicateItems), [duplicateItems]);

  // ----- header facts
  const movedParts = useMemo(() => stockMovedParts(movements), [movements]);
  const onHandTotal = useMemo(() => stock.reduce((sum, item) => sum + Math.max(0, onHandOf(item)), 0), [stock]);
  const lowCount = useMemo(() => stock.filter((item) => stockStatus(item, movedParts) === 'low').length, [movedParts, stock]);
  const lastReceipt = useMemo(() => groupMovements(movements).find((group) => group.kind === 'GRN'), [movements]);
  const headerSubtitle = (
    <>
      Stock across {INVENTORY_LOCATIONS.length} locations · {formatQty(onHandTotal)} pcs on hand
      {lastReceipt ? ` · last receipt ${formatDate(lastReceipt.date)} (${documentLabel(lastReceipt)}${lastReceipt.user ? ` · ${lastReceipt.user}` : ''})` : ' · no receipts recorded yet'}
    </>
  );

  /** Add items grid (Catalog idea 7): only the clean lines arrive here, saved in one batch. */
  const handleSaveCatalogItems = async (items) => {
    if (items.length === 1) {
      await api.post('/catalog', items[0]);
    } else {
      await api.post('/catalog/batch', { items });
    }
    toast.success(items.length === 1 ? 'Item added' : `${items.length} items added`);
    setCatalogDialog(false);
    refreshCatalog();
    fetchData();
  };

  // Selection survives paging: the item itself is remembered so Merge sees every selected row.
  const toggleCatalogSelection = (itemId, checked, item) => {
    setSelectedCatalogIds((prev) => (
      checked ? [...new Set([...prev, itemId])] : prev.filter((id) => id !== itemId)
    ));
    const known = item || catalog.find((entry) => entry.id === itemId);
    setSelectedCatalogItemsById((prev) => {
      const next = { ...prev };
      if (checked && known) next[itemId] = known; else delete next[itemId];
      return next;
    });
  };

  const handleToggleAllCatalogSelection = (checked) => {
    setSelectedCatalogIds((prev) => (
      checked
        ? [...new Set([...prev, ...catalog.map((item) => item.id)])]
        : prev.filter((id) => !catalog.some((item) => item.id === id))
    ));
    setSelectedCatalogItemsById((prev) => {
      const next = { ...prev };
      catalog.forEach((item) => {
        if (checked) next[item.id] = item; else delete next[item.id];
      });
      return next;
    });
  };

  const clearCatalogSelection = () => {
    setSelectedCatalogIds([]);
    setSelectedCatalogItemsById({});
  };

  const toggleStockSelection = (partNumber, checked) => {
    setSelectedStockPartNumbers((prev) => (
      checked ? [...new Set([...prev, partNumber])] : prev.filter((value) => value !== partNumber)
    ));
  };

  const handleToggleAllStockSelection = (checked, visibleItems = stock) => {
    setSelectedStockPartNumbers((prev) => (
      checked
        ? [...new Set([...prev, ...visibleItems.map((item) => item.part_number)])]
        : prev.filter((partNumber) => !visibleItems.some((item) => item.part_number === partNumber))
    ));
  };

  const stockEditFields = ['part_number', 'description', 'brand', 'category'];
  /** Fields of the open editor that differ from the saved item (trimmed). */
  const stockEditChanges = (edit) => {
    if (!edit) return {};
    const original = createStockEditDraft(stockByPartNumber.get(edit.partNumber) || { part_number: edit.partNumber });
    return Object.fromEntries(stockEditFields
      .map((field) => [field, String(edit.draft?.[field] ?? '').trim()])
      .filter(([field, value]) => value !== String(original[field] ?? '').trim()));
  };

  /** Opens the part-number fix on one row (lines without a catalog record). Selection is cleared and locked. */
  const handleStartStockInlineEdit = async (partNumber) => {
    const item = stockByPartNumber.get(partNumber);
    if (!item) return;
    if (stockEdit && stockEdit.partNumber !== partNumber && Object.keys(stockEditChanges(stockEdit)).length) {
      const discard = await confirm({
        title: `Discard your changes to ${stockEdit.partNumber || 'this item'}?`,
        description: 'Only one item can be edited at a time.',
        confirmLabel: 'Discard changes',
        tone: 'danger'
      });
      if (!discard) return;
    }
    setSelectedStockPartNumbers([]);
    setStockExpanded(null);
    setStockEdit({ partNumber, draft: createStockEditDraft(item) });
  };

  const handleStockDraftChange = (partNumber, field, value) => {
    setStockEdit((prev) => {
      // A draft always starts from the saved item, so untouched fields are never sent blank.
      const draft = prev?.partNumber === partNumber ? prev.draft : createStockEditDraft(stockByPartNumber.get(partNumber));
      return { partNumber, draft: { ...draft, [field]: value } };
    });
  };

  const handleCancelStockInlineEdit = useCallback(() => setStockEdit(null), []);

  const handleSaveStockInlineEdit = async () => {
    if (!stockEdit) return;
    const changes = stockEditChanges(stockEdit);
    if ('part_number' in changes && !changes.part_number) {
      toast.error('Part number cannot be empty');
      return;
    }
    if (!Object.keys(changes).length) {
      setStockEdit(null);
      toast.info('No changes to save');
      return;
    }
    const originalPartNumber = stockEdit.partNumber;
    try {
      // Only the edited fields are sent. Quantities never change here — only through movements.
      await api.put(`/stock/${encodeURIComponent(originalPartNumber)}`, changes);
      // Recorded in undo history: the shell adds the Undo for exactly this change.
      notify.success(`Saved ${changes.part_number || originalPartNumber || 'item'}`);
      setStockEdit(null);
      fetchData();
      refreshCatalog();
    } catch (err) {
      notify.error('Could not save the item', { description: getErrorMessage(err) });
    }
  };

  /**
   * Adjust stock (Stock idea 5): an Adjustment movement with a reason — never a typed-over balance.
   * The reason goes in `reason` only: as a `reference` it would read like a document number.
   */
  const handleAdjustStock = async ({ part_number: partNumber, location, qty, reason }) => {
    await api.post('/stock/movement', {
      part_number: partNumber,
      movement_type: 'adjustment',
      qty,
      source_location: location,
      destination_location: location,
      reason,
      reference: '',
      document_id: newDocumentId(),
      delivery_type: 'to_stock'
    });
    notify.success(`${partNumber}: ${qty > 0 ? '+' : '−'}${formatQty(Math.abs(qty))} in ${location} · ${reason}`);
    fetchData();
  };

  /** Reorder list "Set" (Dashboard idea 3) and the Catalog drawer both save through PUT /catalog/{id}. */
  const handleSetMinLevel = async (item, value) => {
    const catalogId = item.catalog_id || item.id;
    await api.put(`/catalog/${catalogId}`, { min_stock_level: value });
    notify.success(value ? `Min level of ${item.part_number} set to ${formatNumber(value)}` : `Min level of ${item.part_number} cleared (default 5)`);
    fetchData();
    refreshCatalog();
  };

  // The drawer stays open beside the list: opening another row while it has edits asks first.
  const catalogDrawerDirtyRef = useRef(0);
  const handleCatalogDrawerDirty = useCallback((count) => { catalogDrawerDirtyRef.current = count; }, []);
  const openCatalogItem = useCallback(async (item) => {
    if (!item) return;
    // From the Stock tab the row carries the catalog fields; its id is the catalog id.
    const catalogItem = item.catalog_id ? { ...item, id: item.catalog_id } : item;
    if (!catalogItem.id) {
      toast.error('This stock line has no catalog record — fix its part number first.');
      return;
    }
    if (catalogDrawerItem && catalogDrawerItem.id !== catalogItem.id && catalogDrawerDirtyRef.current > 0) {
      const discard = await confirm({
        title: `Discard your changes to ${catalogDrawerItem.part_number}?`,
        description: 'The drawer shows one item at a time.',
        confirmLabel: 'Discard changes',
        tone: 'danger'
      });
      if (!discard) return;
    }
    setCatalogDrawerItem(catalogItem);
  }, [catalogDrawerItem, confirm]);

  const handleSaveCatalogItem = async (item, payload) => {
    const res = await api.put(`/catalog/${item.id}`, payload);
    notify.success(`Saved ${res.data?.part_number || item.part_number}`);
    refreshCatalog();
    fetchData();
  };

  /** Bulk Set category / Set brand (Catalog idea 6) — one request, one undo entry. */
  const handleBulkSetField = async (field, value) => {
    const ids = [...selectedCatalogIds];
    await api.put('/catalog/bulk-update', { item_ids: ids, [field]: value });
    notify.success(`${field === 'brand' ? 'Brand' : 'Category'} set to “${value}” on ${plural(ids.length, 'item')}`);
    refreshCatalog();
    fetchData();
  };

  const exportSelectedCatalog = () => {
    const columns = [
      { header: 'Part number', value: (row) => row.part_number },
      { header: 'Description', value: (row) => row.description || '' },
      { header: 'Brand', value: (row) => row.brand || '' },
      { header: 'Category', value: (row) => row.category || '' },
      { header: 'Unit', value: (row) => row.unit || 'pcs' },
      { header: 'Min level', numeric: true, value: (row) => Number(row.min_stock_level || 0) || '' },
      { header: 'Lead time (days)', numeric: true, value: (row) => Number(row.lead_time_days || 0) || '' },
      { header: 'Preferred supplier', value: (row) => row.preferred_supplier || '' }
    ];
    downloadXlsx({ filename: 'catalog-selection', sheetName: 'Catalog', columns, rows: selectedCatalogItems });
  };

  /** Delete catalog items (Admin / Super Admin / Storekeeper — the server refuses everyone else). */
  const handleDeleteSelectedCatalogItems = async (itemIds) => {
    const ids = [...new Set((itemIds || []).filter(Boolean))];
    if (!ids.length) {
      toast.error('Select inventory items first');
      return;
    }
    const partNumbers = ids
      .map((id) => catalog.find((item) => item.id === id)?.part_number
        || selectedCatalogItemsById[id]?.part_number
        || stock.find((item) => item.catalog_id === id)?.part_number)
      .filter(Boolean);
    const affectedStock = stock.filter((item) => partNumbers.includes(item.part_number));
    const pcs = affectedStock.reduce((sum, item) => sum + Math.max(0, onHandOf(item)), 0);
    const reserved = affectedStock.reduce((sum, item) => sum + reservedOf(item), 0);
    const movementCount = movements.filter((movement) => partNumbers.includes(movement.part_number)).length;
    const countText = String(ids.length);

    const ok = await confirm({
      title: `Delete ${plural(ids.length, 'inventory item')}?`,
      subtitle: partNumbers.slice(0, 4).join(', ') + (partNumbers.length > 4 ? ` +${partNumbers.length - 4} more` : ''),
      description: 'This removes the items from the catalog together with:',
      consequences: [
        { icon: Package, count: formatQty(pcs), label: 'pcs of stock on hand' },
        { icon: Lock, count: formatQty(reserved), label: 'pcs reserved for projects' },
        { icon: History, count: formatNumber(movementCount), label: 'movement records' }
      ],
      tone: 'danger',
      confirmLabel: `Delete ${plural(ids.length, 'item')}`,
      requireText: countText,
      requireTextLabel: `Type ${countText} to confirm`,
      note: 'Only Admin, Super Admin and Storekeeper can delete inventory items. You can undo from the message that follows.'
    });
    if (!ok) return;

    try {
      await api.post('/catalog/bulk-delete', { item_ids: ids });
      // One bulk request = one undo entry: the shell's toast undoes exactly this delete.
      notify.success(`Deleted ${plural(ids.length, 'inventory item')}`);
      setSelectedCatalogIds((prev) => prev.filter((id) => !ids.includes(id)));
      setSelectedCatalogItemsById((prev) => {
        const next = { ...prev };
        ids.forEach((id) => { delete next[id]; });
        return next;
      });
      setSelectedStockPartNumbers((prev) => prev.filter((partNumber) => !partNumbers.includes(partNumber)));
      if (stockEdit && partNumbers.includes(stockEdit.partNumber)) setStockEdit(null);
      if (catalogDrawerItem && ids.includes(catalogDrawerItem.id)) setCatalogDrawerItem(null);
      refreshCatalog();
      fetchData();
    } catch (err) {
      notify.error('Could not delete the selected items', { description: getErrorMessage(err) });
    }
  };

  const handleDeleteStockItems = (partNumbers) => {
    const ids = partNumbers
      .map((partNumber) => stockByPartNumber.get(partNumber)?.catalog_id
        || catalog.find((item) => item.part_number === partNumber)?.id)
      .filter(Boolean);
    const missing = partNumbers.length - ids.length;
    if (!ids.length) {
      toast.error('These stock lines have no catalog record to delete — fix their part number instead.');
      return;
    }
    if (missing > 0) toast.info(`${plural(missing, 'line')} without a catalog record will stay — fix their part number instead.`);
    handleDeleteSelectedCatalogItems(ids);
  };

  const handleCheckCatalogDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const res = await api.get('/catalog/duplicates');
      const groups = res.data?.groups || [];
      setDuplicateGroups(groups);
      setDuplicatesDialogOpen(true);
      // The same groups the review dialog lists (a group found both ways is counted once).
      const review = duplicateReviewGroups([...duplicateKeys.values()], groups);
      const countOf = (kind) => review.filter((group) => group.kind === kind).length;
      if (review.length) {
        notify.success(`Found ${plural(review.length, 'group')} to review`, {
          description: [
            countOf('exact') ? `${formatNumber(countOf('exact'))} same part number` : '',
            countOf('normalized') ? `${formatNumber(countOf('normalized'))} matching only when - and spaces are ignored` : '',
            countOf('similar') ? `${plural(countOf('similar'), 'similar description')}` : ''
          ].filter(Boolean).join(' · ')
        });
      } else {
        notify.success('No duplicate groups found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to check duplicates'));
    } finally {
      setDuplicatesLoading(false);
    }
  };

  /**
   * Merge a duplicate group by id (Catalog idea 4) — from the review dialog, a row's menu or the drawer.
   * The survivor keeps its part number and description; items on other pages are included.
   */
  const handleMergeGroup = async (items, survivorId) => {
    const group = (items || []).filter(Boolean);
    if (group.length < 2) {
      toast.error('A group needs at least two items to merge');
      return;
    }
    const survivor = group.find((item) => item.id === survivorId) || group[0];
    const ok = await confirm({
      title: `Merge ${plural(group.length, 'item')} into ${survivor.part_number}?`,
      subtitle: group.map((item) => item.part_number).join(', '),
      description: 'Project BOQs, reservations, movements and delivery notes move to the item you keep, and stock is combined per location.',
      icon: GitMerge,
      confirmLabel: 'Merge items',
      note: 'Only Admin, Super Admin and Storekeeper can merge. You can undo from the message that follows.'
    });
    if (!ok) return;
    try {
      await api.post('/catalog/merge', {
        item_ids: group.map((item) => item.id),
        survivor_item_id: survivor.id,
        final_part_number: String(survivor.part_number || '').trim(),
        final_description: String(survivor.description || '').trim()
      });
      notify.success(`Merged ${plural(group.length, 'item')} into ${survivor.part_number}`);
      const mergedIds = new Set(group.map((item) => item.id));
      setDuplicateGroups((groups) => groups.filter((entry) => !(entry.items || []).some((item) => mergedIds.has(item.id))));
      setSelectedCatalogIds((prev) => prev.filter((id) => !mergedIds.has(id)));
      if (catalogDrawerItem && mergedIds.has(catalogDrawerItem.id)) setCatalogDrawerItem(null);
      refreshCatalog();
      fetchData();
    } catch (err) {
      notify.error('Could not merge the items', { description: getErrorMessage(err) });
    }
  };

  const handleOpenMergeDialog = () => {
    if (selectedCatalogItems.length < 2) {
      toast.error('Select at least two catalog items to merge');
      return;
    }
    const survivor = selectedCatalogItems[0];
    setMergeForm({
      survivor_item_id: survivor.id,
      final_part_number: survivor.part_number || '',
      final_description: survivor.description || ''
    });
    setMergeDialogOpen(true);
  };

  const handleMergeFormChange = (updates) => {
    setMergeForm((prev) => {
      const next = { ...prev, ...updates };
      if (updates.survivor_item_id) {
        const survivor = selectedCatalogItems.find((item) => item.id === updates.survivor_item_id);
        if (survivor) {
          next.final_part_number = survivor.part_number || '';
          next.final_description = survivor.description || '';
        }
      }
      return next;
    });
  };

  const handleMergeCatalogItems = async () => {
    if (selectedCatalogItems.length < 2) {
      toast.error('Select at least two catalog items to merge');
      return;
    }
    if (!String(mergeForm.final_part_number || '').trim()) {
      toast.error('Final part number is required');
      return;
    }
    const ok = await confirm({
      title: `Merge ${plural(selectedCatalogItems.length, 'catalog item')} into ${String(mergeForm.final_part_number).trim()}?`,
      description: 'Project BOQs, reservations, movements and delivery notes are re-pointed to the surviving item, and stock quantities are combined per location.',
      confirmLabel: 'Merge items'
    });
    if (!ok) return;

    try {
      await api.post('/catalog/merge', {
        item_ids: selectedCatalogIds,
        survivor_item_id: mergeForm.survivor_item_id,
        final_part_number: String(mergeForm.final_part_number || '').trim(),
        final_description: String(mergeForm.final_description || '').trim()
      });
      notify.success('Catalog items merged');
      setMergeDialogOpen(false);
      clearCatalogSelection();
      refreshCatalog();
      fetchData();
    } catch (err) {
      notify.error('Could not merge the items', { description: getErrorMessage(err) });
    }
  };

  const exportCatalog = async (format) => {
    try {
      const rows = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await api.get('/catalog', { params: { ...catalogParams, include_meta: false, page, limit: 200 } });
        rows.push(...(res.data?.items || []));
        totalPages = res.data?.total_pages || 1;
        page += 1;
      } while (page <= totalPages);
      const columns = [
        { header: 'Part number', value: (row) => row.part_number },
        { header: 'Description', value: (row) => row.description || '' },
        { header: 'Brand', value: (row) => row.brand || '' },
        { header: 'Category', value: (row) => row.category || '' },
        { header: 'Unit', value: (row) => row.unit || 'pcs' },
        { header: 'Min level', numeric: true, value: (row) => Number(row.min_stock_level || 0) || '' },
        { header: 'Lead time (days)', numeric: true, value: (row) => Number(row.lead_time_days || 0) || '' },
        { header: 'Preferred supplier', value: (row) => row.preferred_supplier || '' }
      ];
      const payload = { filename: 'catalog', sheetName: 'Catalog', columns, rows };
      if (format === 'csv') downloadCsv(payload); else downloadXlsx(payload);
      notify.success(`Exported ${plural(rows.length, 'catalog item')}`);
    } catch (err) {
      notify.error('Could not export the catalog', { description: getErrorMessage(err) });
    }
  };

  const handleMovementRowChange = (index, field, value) => {
    setMovementRows((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )));
  };

  const handleAddMovementRow = () => {
    setMovementRows((prev) => [...prev, createMovementRow()]);
  };

  const handleRemoveMovementRow = (index) => {
    setMovementRows((prev) => (prev.length === 1 ? prev : prev.filter((_, rowIndex) => rowIndex !== index)));
  };

  /**
   * "Paste list" in Record movement: part no. + qty per line; unknown part numbers are named. A Site DN
   * follows the project's BOQ (Movements idea 6): pasted items the BOQ does not list are named and left out.
   */
  const handlePasteMovementRows = (raw) => {
    const parsed = parsePastedRows(raw, ['part_number', 'qty'], {
      part_number: ['part', 'model', 'item', 'code'],
      qty: ['qty', 'quantity', 'pcs']
    }).filter((row) => row.part_number);
    const siteDn = movementForm.movement_type === 'issue' && Boolean(movementForm.project_id);
    if (siteDn && movementProjectBoqStatus === 'loading') {
      notify.info('The project’s BOQ is still loading — paste again in a moment');
      return;
    }
    const known = new Map(inventoryItemOptions.map((item) => [normalizePartNumber(item.part_number), item.part_number]));
    const boqKeys = siteDn && movementProjectBoqStatus === 'loaded' ? movementBoqPartKeys : null;
    const unknown = [];
    const notInBoq = [];
    const lines = [];
    parsed.forEach((row) => {
      const key = normalizePartNumber(row.part_number);
      const match = known.get(key);
      if (!match) { unknown.push(row.part_number); return; }
      if (boqKeys && !boqKeys.has(key)) { notInBoq.push(match); return; }
      lines.push(createMovementRow(match, toQty(row.qty)));
    });
    if (lines.length) {
      setMovementRows((prev) => [...prev.filter((row) => row.part_number || Number(row.qty)), ...lines]);
    }
    if (notInBoq.length) {
      const projectName = projectById.get(movementForm.project_id)?.name || 'this project';
      notify.warning(`${plural(notInBoq.length, 'item')} not in the BOQ of ${projectName} — left out`, { description: notInBoq.slice(0, 8).join(', ') });
    }
    if (unknown.length) {
      notify.warning(`${plural(unknown.length, 'part number')} not in the catalog`, { description: unknown.slice(0, 8).join(', ') });
    } else if (lines.length && !notInBoq.length) {
      notify.info(`Added ${plural(lines.length, 'line')}`);
    }
  };

  const getMovementRowAvailability = (row) => {
    const sourceLocation = movementForm.source_location;
    const qty = Number(row.qty || 0);
    const useSource = ['transfer', 'issue', 'testing'].includes(movementForm.movement_type)
      || (movementForm.movement_type === 'adjustment' && qty < 0);
    const { qty: availableQty, booked } = useSource
      ? getMovementStock(row.part_number, sourceLocation)
      : { qty: getAvailableQty(row.part_number, ''), booked: 0 };
    const bookedNote = booked > 0 ? ` (${formatQty(booked)} booked)` : '';
    const requestedQty = Math.abs(qty);
    if (!row.part_number) return { tone: 'neutral', text: 'Select item to check stock', availableQty };
    if (!useSource && movementForm.movement_type === 'adjustment') {
      return { tone: 'green', text: 'Adjustment will add stock at this location', availableQty };
    }
    if (!useSource) return { tone: 'green', text: `${availableQty} available`, availableQty };
    if (availableQty === 0) return { tone: 'red', text: 'No stock available', availableQty };
    if (requestedQty > availableQty) return { tone: 'red', text: `Cannot proceed - only ${availableQty} available${bookedNote}`, availableQty };
    if (requestedQty > availableQty * 0.7) return { tone: 'amber', text: `${requestedQty} of ${availableQty} available${bookedNote} - low after movement`, availableQty };
    return { tone: 'green', text: `OK - ${availableQty} available${bookedNote}`, availableQty };
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    if (pendingDrop) {
      toast.error('Decide about the items that are not in the project BOQ first');
      return;
    }
    const validRows = movementRows
      .map((row) => ({ part_number: String(row.part_number || '').trim(), qty: Number(row.qty || 0) }))
      .filter((row) => row.part_number && (movementForm.movement_type === 'adjustment' ? row.qty !== 0 : row.qty > 0));

    if (!validRows.length) {
      toast.error('Add at least one item and quantity');
      return;
    }
    if (movementForm.movement_type === 'adjustment' && !String(movementForm.reference || '').trim()) {
      toast.error('Give a reason for the adjustment');
      return;
    }

    const invalidRow = validRows.find((row) => {
      const availability = getMovementRowAvailability(row);
      return availability.tone === 'red' && (
        ['transfer', 'issue', 'testing'].includes(movementForm.movement_type)
        || (movementForm.movement_type === 'adjustment' && row.qty < 0)
      );
    });
    if (invalidRow) {
      toast.error('One or more rows exceed available stock');
      return;
    }

    setMovementSubmitting(true);
    try {
      // For an adjustment the text field holds the reason; it is not a document number.
      const isAdjustment = movementForm.movement_type === 'adjustment';
      const typed = String(movementForm.reference || '').trim();
      const reference = isAdjustment ? '' : typed;
      // Every line of this submission is one document in the ledger (Reverse / Print act on exactly these).
      const documentId = newDocumentId();
      const results = await Promise.allSettled(validRows.map((row) => api.post('/stock/movement', {
        ...movementForm,
        part_number: row.part_number,
        qty: row.qty,
        reference,
        reason: isAdjustment ? typed : '',
        document_id: documentId,
        notes: String(movementForm.notes || '').trim()
      })));

      const failedResults = results
        .map((result, index) => ({ result, row: validRows[index] }))
        .filter(({ result }) => result.status === 'rejected');

      const successfulRows = validRows.filter((_, index) => results[index].status === 'fulfilled');
      const movementIds = results.filter((result) => result.status === 'fulfilled').map((result) => result.value?.data?.id).filter(Boolean);

      if (failedResults.length > 0) {
        const failedList = failedResults
          .map(({ row, result }) => `${row.part_number || 'row'}: ${getErrorMessage(result.reason, 'Failed')}`)
          .join('; ');

        toast.error(`${successfulRows.length} row(s) recorded, ${failedResults.length} failed: ${failedList}`);
      } else {
        toast.success('Stock movement recorded');
      }

      if (successfulRows.length > 0) {
        if (convertingPlan && movementForm.movement_type === 'issue') {
          // The plan closes only when every line was issued; otherwise it stays a draft with the lines left.
          const left = new Map();
          failedResults.forEach(({ row }) => left.set(row.part_number, (left.get(row.part_number) || 0) + row.qty));
          const remainingItems = [...left.entries()].map(([partNumber, qty]) => ({ part_number: partNumber, qty }));
          try {
            await api.post(`/delivery-plans/${convertingPlan.id}/converted`, {
              dn_reference: reference,
              movement_ids: movementIds,
              ...(remainingItems.length ? { remaining_items: remainingItems } : {})
            });
            if (remainingItems.length) {
              notify.warning(`Delivery plan for ${convertingPlan.project_name} stays a draft with ${plural(remainingItems.length, 'item')} not issued`, {
                description: remainingItems.map((item) => `${item.part_number} × ${formatQty(item.qty)}`).join(', ')
              });
            } else {
              notify.success(`Delivery plan for ${convertingPlan.project_name} converted to DN ${reference}`);
            }
          } catch (err) {
            notify.error('The DN is recorded, but the plan could not be updated', { description: getErrorMessage(err) });
          }
          setConvertingPlan(null);
          fetchDeliveryPlans();
        }
        if (movementForm.movement_type === 'issue') {
          setDeliveryPreview({
            dn_number: reference,
            date: new Date().toISOString().split('T')[0],
            from: movementForm.source_location,
            to: 'Site',
            project: projectById.get(movementForm.project_id)?.name || movementForm.project_id,
            authorized_by: user?.name || user?.email || '-',
            notes: String(movementForm.notes || '').trim(),
            items: successfulRows.map((row) => ({
              ...row,
              description: stockByPartNumber.get(row.part_number)?.description || ''
            }))
          });
        } else if (failedResults.length === 0) {
          closeMovementDialog();
        }
        setSelectedStockPartNumbers([]);
        fetchData();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed'));
    } finally {
      setMovementSubmitting(false);
    }
  };

  const printPreviewDn = () => {
    if (!deliveryPreview) return;
    const ok = printStockDocument({
      kind: 'site-dn',
      label: `DN ${deliveryPreview.dn_number}`,
      date: deliveryPreview.date,
      from: deliveryPreview.from,
      to: deliveryPreview.project,
      project: deliveryPreview.project,
      by: deliveryPreview.authorized_by,
      notes: deliveryPreview.notes,
      lines: deliveryPreview.items
    });
    if (!ok) notify.error('Allow pop-ups for this site to print the DN');
  };

  /** Print a ledger document as a real GRN / DN (Movements idea 3). */
  const printMovementDocument = (group) => {
    const projectName = projectById.get(group.projectId)?.name || group.movements[0]?.project_name || '';
    const facts = documentFacts(group, projectName);
    const receipt = siteDnReceipt(group);
    const doc = {
      kind: group.kind,
      label: documentLabel(group),
      date: group.date,
      from: facts.from,
      to: facts.to,
      project: projectName,
      po: group.movements[0]?.lpo_number || '',
      by: group.user,
      notes: group.notes.join(' · '),
      lines: group.movements.map((movement) => ({
        part_number: movement.part_number,
        description: movement.description || itemDescriptions[movement.part_number] || '',
        qty: movement.qty
      }))
    };
    if (receipt?.state === 'received') {
      // A DN signed on site prints with the receiver's name, time and signature (21 #9).
      printStockDocumentWhenReady(doc, async () => {
        let signature = '';
        if (receipt.signatureUrl) {
          const res = await api.get(receipt.signatureUrl.replace(/^\/api/, ''), { responseType: 'blob' });
          signature = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => resolve('');
            reader.readAsDataURL(res.data);
          });
        }
        return { receipt: { name: receipt.receivedBy, at: receipt.receivedAt, notes: receipt.notes, signature } };
      }).then((ok) => { if (!ok) notify.error('Allow pop-ups for this site to print the document'); });
      return;
    }
    const ok = printStockDocument(doc);
    if (!ok) notify.error('Allow pop-ups for this site to print the document');
  };

  const handleReserve = async (e) => {
    e.preventDefault();
    if (!reserveForm.project_id || !reserveForm.part_number || reserveQty <= 0) {
      toast.error('Select a project, item, and quantity before reserving');
      return;
    }
    if (reserveHasStockError) {
      toast.error('Reduce quantity before reserving');
      return;
    }
    if (!reserveConfirming) {
      setReserveConfirming(true);
      return;
    }
    try {
      await api.post('/reservations', { ...reserveForm, qty_reserved: Number(reserveForm.qty_reserved) });
      toast.success('Stock reserved');
      setReserveDialog(false);
      resetReserveModal();
      setSelectedStockPartNumbers([]);
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err, 'Failed')); }
  };

  /** Release a booking (Dashboard idea 7): the pieces go back to available; the booking stays in history. */
  const handleReleaseBooking = async (reservation) => {
    const ok = await confirm({
      title: `Release ${formatQty(reservation.qty_reserved)} × ${reservation.part_number}?`,
      subtitle: `Booked for ${reservation.project_name || 'a project'}${reservation.needed_by_date ? ` · needed by ${formatDate(reservation.needed_by_date)}` : ''}`,
      description: 'The pieces become available again for other projects. The booking stays in the history as Released.',
      icon: Unlock,
      confirmLabel: 'Release booking'
    });
    if (!ok) return;
    try {
      await api.put(`/reservations/${reservation.id}`, { status: 'Released' });
      notify.success(`Released ${formatQty(reservation.qty_reserved)} × ${reservation.part_number}`);
      fetchData();
    } catch (err) {
      notify.error('Could not release the booking', { description: getErrorMessage(err) });
    }
  };

  const handleIssueBooking = (reservation) => {
    openMovementDialog('issue', [{ part_number: reservation.part_number, qty: toQty(reservation.qty_reserved) }], {
      projectId: reservation.project_id,
      sourceLocation: reservation.location
    });
  };

  // ----- delivery plans (Stock idea 8)
  // Same rule as the server (_can_edit_plan): the plan's author, Admin, Super Admin or Storekeeper.
  const canChangePlan = useCallback((plan) => canEdit && (canManageStock || Boolean(plan.created_by_id && plan.created_by_id === userKey(user))), [canEdit, canManageStock, user]);

  const handleSavePlan = async (payload) => {
    const editing = planEditor.plan?.id;
    if (editing) {
      await api.put(`/delivery-plans/${editing}`, payload);
      notify.success('Delivery plan saved');
    } else {
      await api.post('/delivery-plans', payload);
      notify.success(`Delivery plan saved for ${projectById.get(payload.project_id)?.name || 'the project'}`);
    }
    fetchDeliveryPlans();
  };

  const handleCancelPlan = async (plan) => {
    const ok = await confirm({
      title: `Cancel the delivery plan for ${plan.project_name}?`,
      subtitle: `${formatDate(plan.planned_date)} · ${plural(plan.item_count, 'item')}`,
      description: 'The plan leaves the list. Nothing in stock changes.',
      tone: 'danger',
      confirmLabel: 'Cancel plan',
      cancelLabel: 'Keep plan'
    });
    if (!ok) return;
    try {
      await api.delete(`/delivery-plans/${plan.id}`);
      // Cancelling is recorded in undo history (delivery_plans is in the undo registry), so the shell's
      // toast offers Undo for this exact change — no page-level Restore here.
      notify.success('Delivery plan cancelled');
      fetchDeliveryPlans();
    } catch (err) {
      notify.error('Could not cancel the plan', { description: getErrorMessage(err) });
    }
  };

  const handleConvertPlan = (plan) => {
    setPlansPanelOpen(false);
    openMovementDialog('issue', (plan.items || []).map((line) => ({ part_number: line.part_number, qty: line.qty })), {
      projectId: plan.project_id,
      sourceLocation: plan.source_location || undefined,
      notes: plan.notes,
      plan
    });
  };

  /** Dashboard / callout links: open a tab with the matching filter already applied ('reserved' view too). */
  const navigateTo = useCallback((tab, filters = {}) => {
    if (tab === 'stock') {
      setStockLocationFilter(filters.location || null);
      setStockProjectFilter(null);
      setStockNeedsFix(Boolean(filters.needsFix));
      setStockQbDiffers(false);
      if (filters.view) setStockQuickFilter(filters.view);
    }
    if (tab === 'sites-boq' && filters.status) setSitesBoqStatus(filters.status);
    if (tab === 'sites-boq' && filters.search !== undefined) setSearch(filters.search);
    if (tab === 'sites-boq' && filters.project !== undefined) setSitesBoqProject(filters.project || null);
    if (tab === 'movements' && filters.type) setMovementTypeFilter(filters.type);
    if (tab === 'movements' && filters.doc) setMovementDocKey(filters.doc);
    if (tab === 'catalog') setCatalogHealthFilter(filters.health || null);
    setActiveTab(tab);
    // From a card low on the Dashboard the window is scrolled: bring the tabs (and the new tab's filters) into view.
    const scrollUp = () => {
      const bar = tabsBarRef.current;
      if (!bar || typeof bar.getBoundingClientRect !== 'function') return;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--app-header-height')) || 0;
      if (bar.getBoundingClientRect().top < header) bar.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(scrollUp); else scrollUp();
  }, []);

  /**
   * Ledger / document panel "Fix in Catalog": the item's catalog record opens in the Catalog drawer; a line
   * with no catalog record (a '-' typed on a GRN) opens its editor on the Stock tab's "Needs fixing" list.
   */
  const fixInCatalog = (partNumber) => {
    const item = stockByPartNumber.get(partNumber);
    if (item?.catalog_id) {
      navigateTo('catalog');
      openCatalogItem(item);
      return;
    }
    navigateTo('stock', { view: 'all', needsFix: true });
    if (item) handleStartStockInlineEdit(partNumber);
  };
  const fixLabelOf = (partNumber) => (stockByPartNumber.get(partNumber)?.catalog_id ? 'Fix in Catalog' : 'Fix in Stock');

  /**
   * Movements period (21 #2): the page holds the newest 2,000 lines; a period that reaches further back than
   * them is loaded from the server (date_from), and "All time" goes back to the newest lines.
   */
  const handleMovementPeriod = useCallback(async (period) => {
    const option = MOVEMENT_PERIODS.find((entry) => entry.value === period);
    if (!option) {
      if (!movementsSinceRef.current) return;
      movementsSinceRef.current = null;
      try {
        const res = await api.get('/stock/movements');
        setMovements(res.data || []);
        setMovementsCapped((res.data || []).length >= MOVEMENTS_DEFAULT_LIMIT);
      } catch (err) {
        notify.error('Could not load the movements', { description: getErrorMessage(err) });
      }
      return;
    }
    if (!movementsCapped && !movementsSinceRef.current) return;
    const from = new Date();
    from.setDate(from.getDate() - option.days);
    movementsSinceRef.current = toDateInputValue(from);
    try {
      const res = await api.get('/stock/movements', { params: { date_from: movementsSinceRef.current, limit: MOVEMENTS_PERIOD_LIMIT } });
      setMovements(res.data || []);
      setMovementsCapped(false);
    } catch (err) {
      notify.error('Could not load that period', { description: getErrorMessage(err) });
    }
  }, [movementsCapped]);

  // A reminder / "DN received" notification links to ?tab=movements&dn=<id>: switch to Movements, open that
  // DN's panel (its newest document), then drop ?dn. Read from the URL every time it changes — the bell
  // navigates within the app, so the page is often open already (the storekeeper who dispatched the DN).
  // A link that arrives while the page is open first reloads the ledger, so the panel shows the new receipt (and a
  // DN dispatched since the page loaded is found).
  const handledDnRef = useRef('');
  const refreshingDnRef = useRef('');
  const [refreshedDn, setRefreshedDn] = useState('');
  useEffect(() => {
    if (!linkedDnId) { handledDnRef.current = ''; return; }
    setActiveTab('movements');
    // Opened once the ledger has loaded, and once per link (the same link clicked again later works again).
    if (loading || handledDnRef.current === linkedDnId) return;
    if (mountDnRef.current !== linkedDnId && refreshedDn !== linkedDnId) {
      if (refreshingDnRef.current !== linkedDnId) {
        refreshingDnRef.current = linkedDnId;
        Promise.resolve(fetchData()).finally(() => setRefreshedDn(linkedDnId));
      }
      return;
    }
    handledDnRef.current = linkedDnId;
    refreshingDnRef.current = '';
    mountDnRef.current = '';
    setRefreshedDn('');
    const group = groupMovements(movements).find((entry) => entry.movements.some((movement) => movement.delivery_note_id === linkedDnId));
    if (group) setMovementDocKey(group.key);
    else notify.error('That delivery note is not in the ledger', { description: 'It may have been removed. Reload the page to check.' });
    const next = new URLSearchParams(searchParams);
    next.delete('dn');
    setSearchParams(next, { replace: true });
  }, [fetchData, linkedDnId, loading, movements, refreshedDn, searchParams, setSearchParams]);

  /** Confirm receipt confirms the whole delivery note: the sheet lists every document recorded on it. */
  const openReceiveDn = useCallback((group) => setReceiveDn(siteDeliveryNoteOf(movements, group)), [movements]);

  /** Shelf / bin of an item in one store (18 #10) — printed on its label. */
  const handleSetBin = async ({ part_number: partNumber, location, bin }) => {
    await api.put('/stock-bins', { part_number: partNumber, location, bin });
    notify.success(bin ? `${partNumber}: shelf in ${location} set to ${bin}` : `${partNumber}: shelf in ${location} cleared`);
    fetchData();
  };

  /** Smart catalog (20 #8): accept suggestions — the server fills only fields that are still empty. */
  const handleApplySuggestions = async (items) => {
    const res = await api.post('/catalog/suggestions/apply', { items });
    const data = res.data || {};
    notify.success(`Completed ${plural(data.updated_items || 0, 'catalog item')}`, {
      description: data.skipped_fields ? `${plural(data.skipped_fields, 'field')} left as they were — someone had already filled them in.` : undefined
    });
    refreshCatalog();
    fetchData();
  };

  const receiveDnInfo = receiveDn ? siteDnReceipt(receiveDn) : null;
  const receiveDnProject = receiveDn ? projectById.get(receiveDn.projectId) : null;

  const tabCounts = {
    stock: stock.length,
    'sites-boq': sitesBoq.length,
    catalog: catalogMeta?.catalog_total ?? catalogTotal,
    movements: movements.length
  };
  const tabTriggerClass = 'group relative h-auto rounded-none bg-transparent px-0 pb-[11px] pt-2.5 text-[13.5px] font-semibold text-ink-3 shadow-none ring-offset-0 hover:text-ink-2 data-[state=active]:bg-transparent data-[state=active]:text-ink data-[state=active]:shadow-none after:absolute after:inset-x-0 after:-bottom-px after:h-[2.5px] after:rounded-t after:bg-transparent data-[state=active]:after:bg-brand';
  const tabCountClass = 'ml-1.5 rounded-full bg-[color:var(--status-gray-bg)] px-[7px] py-px text-[11px] font-bold tabular-nums text-ink-2 group-data-[state=active]:bg-brand group-data-[state=active]:text-white';
  const drawerDuplicates = catalogDrawerItem ? duplicateKeys.get(partKey(catalogDrawerItem.part_number)) || [] : [];

  return (
    <div data-testid="inventory-page" className="space-y-5">
      <PageHeader
        title="Inventory"
        count={activeTab === 'catalog' ? `${formatNumber(tabCounts.catalog)} catalog items` : `${formatNumber(stock.length)} stock lines`}
        subtitle={headerSubtitle}
        actions={(
          <>
            {isAdminLike ? (
              <Button variant="ghost" onClick={() => setHistoryDialogOpen(true)} className="px-2.5 text-ink-2 sm:px-3.5" aria-label="History" data-testid="inventory-history-btn">
                <History aria-hidden="true" /><span className="hidden sm:inline">History</span>
              </Button>
            ) : null}
            {canEdit ? (
            <>
            <Button type="button" variant="outline" onClick={() => openReserveDialog()} className="px-2.5 text-ink sm:px-3.5" aria-label="Reserve" data-testid="reserve-stock-btn">
              <Lock aria-hidden="true" /><span className="hidden sm:inline">Reserve</span>
            </Button>
            <Button type="button" variant="outline" onClick={() => setCatalogDialog(true)} className="px-2.5 text-ink sm:px-3.5" aria-label="Add items" data-testid="add-catalog-btn">
              <PackagePlus aria-hidden="true" /><span className="hidden sm:inline">Add items</span>
            </Button>
            <div className="inline-flex">
              <Button type="button" onClick={() => openMovementDialog('GRN')} className="rounded-r-none" data-testid="stock-movement-btn">
                <ArrowRightLeft aria-hidden="true" /> Record movement
              </Button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="icon" className="-ml-px rounded-l-none border-l border-white/20" aria-label="Choose movement type" data-testid="record-movement-menu">
                    <ChevronDown aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[230px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
                  <DropdownMenuLabel className="px-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Record a movement</DropdownMenuLabel>
                  {Object.entries(MOVEMENT_TYPES).map(([value, config]) => {
                    const Icon = config.icon;
                    return (
                      <DropdownMenuItem
                        key={value}
                        className="h-[34px] gap-2.5 rounded-lg text-[13px]"
                        onSelect={() => setTimeout(() => openMovementDialog(value), 0)}
                        data-testid={`record-movement-${value}`}
                      >
                        <Icon className="h-[15px] w-[15px] text-ink-3" aria-hidden="true" /> {config.menuLabel}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            </>
            ) : (
              <span className="inline-flex h-9 items-center rounded-control border border-line bg-surface-2 px-3 text-[12.5px] font-semibold text-ink-3" data-testid="inventory-view-only">
                View only
              </span>
            )}
          </>
        )}
      />

      {isAdminLike ? (
        <HistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          title="Inventory History"
          pageType="inventory"
          entityType="inventory"
          entityId="inventory"
        />
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div ref={tabsBarRef} className="flex scroll-mt-[calc(var(--app-header-height,0px)+12px)] items-end gap-4 border-b border-line">
          <TabsList ref={tabsListRef} className="h-auto min-w-0 flex-1 justify-start gap-[22px] overflow-x-auto rounded-none bg-transparent p-0 text-ink-3">
            <TabsTrigger value="dashboard" className={tabTriggerClass} data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="stock" className={tabTriggerClass} data-testid="tab-stock">
              Stock<span className={tabCountClass}>{formatNumber(tabCounts.stock)}</span>
              {lowCount ? <span className="ml-1.5 text-[11px] font-bold text-status-amber-ink">{lowCount} low</span> : null}
            </TabsTrigger>
            <TabsTrigger value="sites-boq" className={tabTriggerClass} data-testid="tab-sites-boq">
              Sites BOQ<span className={tabCountClass}>{formatNumber(tabCounts['sites-boq'])}</span>
            </TabsTrigger>
            <TabsTrigger value="catalog" className={tabTriggerClass} data-testid="tab-catalog">
              Catalog<span className={tabCountClass}>{formatNumber(tabCounts.catalog)}</span>
            </TabsTrigger>
            <TabsTrigger value="movements" className={tabTriggerClass} data-testid="tab-movements">
              Movements<span className={tabCountClass}>{formatNumber(tabCounts.movements)}</span>
            </TabsTrigger>
          </TabsList>
          <button
            type="button"
            onClick={refreshAll}
            disabled={refreshing}
            className="mb-2 hidden shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-[12px] text-ink-3 hover:bg-surface-hover hover:text-ink-2 disabled:opacity-60 sm:inline-flex"
            title="Reload inventory"
            data-testid="inventory-refresh"
          >
            <RefreshCw className={cn('h-[13px] w-[13px]', refreshing && 'animate-spin')} aria-hidden="true" />
            {loading ? 'Loading…' : refreshing ? 'Updating…' : `Updated ${lastUpdated ? formatTimeAgo(lastUpdated) : '—'}`}
          </button>
        </div>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardTab
            stock={stock}
            movements={movements}
            sitesBoq={sitesBoq}
            projects={projects}
            itemDescriptions={itemDescriptions}
            catalogMeta={catalogMeta}
            reservations={reservations}
            reservationsLoading={reservationsState.loading}
            reservationsError={reservationsState.error}
            onRetryReservations={fetchReservations}
            siteDemandOf={siteDemandOf}
            canEditCatalog={canEditCatalog}
            canEdit={canEdit}
            onSetMin={handleSetMinLevel}
            onReserve={(item) => openReserveDialog(item ? [item.part_number] : [])}
            onIssueBooking={handleIssueBooking}
            onReleaseBooking={handleReleaseBooking}
            onNavigate={navigateTo}
            onOpenMovement={openMovementDialog}
            canRequestPurchase={canRequestPurchase}
            onShortageSent={fetchData}
            onOpenDocument={(key) => navigateTo('movements', { doc: key })}
            onConfirmDn={openReceiveDn}
            dnReminders={dnReminders}
          />
        </TabsContent>

        <TabsContent value="stock" className="mt-4">
          <StockTab
            stockItems={stock}
            movements={movements}
            projects={projects}
            sitesBoq={sitesBoq}
            reservations={reservations}
            siteDemandOf={siteDemandOf}
            search={search}
            onSearchChange={setSearch}
            brandFilter={brandFilter}
            onBrandFilterChange={setBrandFilter}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            quickFilter={stockQuickFilter}
            onQuickFilterChange={setStockQuickFilter}
            locationFilter={stockLocationFilter}
            onLocationFilterChange={setStockLocationFilter}
            projectFilter={stockProjectFilter}
            onProjectFilterChange={setStockProjectFilter}
            needsFix={stockNeedsFix}
            onNeedsFixChange={setStockNeedsFix}
            qb={qbStock}
            qbDiffers={stockQbDiffers}
            onQbDiffersChange={setStockQbDiffers}
            selectedStockPartNumbers={selectedStockPartNumbers}
            toggleStockSelection={toggleStockSelection}
            handleToggleAllStockSelection={handleToggleAllStockSelection}
            onClearSelection={() => setSelectedStockPartNumbers([])}
            editingPartNumber={stockEdit?.partNumber ?? null}
            stockEditDraft={stockEdit?.draft ?? null}
            createStockEditDraft={createStockEditDraft}
            handleStockDraftChange={handleStockDraftChange}
            handleCancelStockInlineEdit={handleCancelStockInlineEdit}
            handleSaveStockInlineEdit={handleSaveStockInlineEdit}
            onEditItem={handleStartStockInlineEdit}
            onEditCatalog={openCatalogItem}
            onAdjust={handleAdjustStock}
            canEditCatalog={canEditCatalog}
            canEdit={canEdit}
            expandedPart={stockExpanded}
            onExpandedPartChange={setStockExpanded}
            onTransfer={(parts) => openMovementDialog('transfer', parts)}
            onIssue={(parts) => openMovementDialog('issue', parts)}
            onReserve={(parts) => openReserveDialog(parts)}
            onDelete={handleDeleteStockItems}
            canDelete={canDeleteItems}
            deliveryPlans={deliveryPlans}
            deliveryPlansLoading={plansState.loading}
            deliveryPlansError={plansState.error}
            onOpenDeliveryPlans={() => setPlansPanelOpen(true)}
            onNewDeliveryPlan={() => setPlanEditor({ open: true, plan: null })}
            onSetBin={canEdit ? handleSetBin : undefined}
          />
        </TabsContent>

        <TabsContent value="sites-boq" className="mt-4">
          <SitesBoqTab
            sitesBoq={sitesBoq}
            stockItems={stock}
            projects={projects}
            search={search}
            onSearchChange={setSearch}
            brandFilter={brandFilter}
            onBrandFilterChange={setBrandFilter}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            statusFilter={sitesBoqStatus}
            onStatusFilterChange={setSitesBoqStatus}
            onReserve={(line, detail, qty) => openReserveDialog([line.part_number], {
              projectId: detail?.project_id,
              boqItemId: detail?.boq_item_id,
              qty
            })}
            onIssue={openSiteDnForLine}
            canRequestPurchase={canRequestPurchase}
            canEdit={canEdit}
            siteRequests={siteRequests}
            onPurchaseRequested={fetchData}
            projectFilter={sitesBoqProject}
            onProjectFilterChange={setSitesBoqProject}
            canAutoBook={canAutoBook}
            onAutoBooked={fetchData}
          />
        </TabsContent>

        <TabsContent value="catalog" className="mt-4">
          <CatalogTab
            catalogItems={catalog}
            catalogTotal={catalogTotal}
            catalogMeta={catalogMeta}
            catalogPage={catalogPage}
            catalogTotalPages={catalogTotalPages}
            setCatalogPage={setCatalogPage}
            catalogLimit={catalogLimit}
            onCatalogLimitChange={setCatalogLimit}
            catalogLoading={catalogLoading}
            search={search}
            onSearchChange={setSearch}
            brandFilter={brandFilter}
            onBrandFilterChange={setBrandFilter}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            supplierFilter={catalogSupplierFilter}
            onSupplierFilterChange={setCatalogSupplierFilter}
            healthFilter={catalogHealthFilter}
            onHealthFilterChange={setCatalogHealthFilter}
            onExport={exportCatalog}
            onExportSelected={exportSelectedCatalog}
            selectedCatalogIds={selectedCatalogIds}
            onClearSelection={clearCatalogSelection}
            canDelete={canDeleteItems}
            canMerge={canDeleteItems}
            handleDeleteSelectedCatalogItems={handleDeleteSelectedCatalogItems}
            allCatalogSelected={allCatalogSelected}
            handleToggleAllCatalogSelection={handleToggleAllCatalogSelection}
            toggleCatalogSelection={toggleCatalogSelection}
            duplicateGroups={duplicateGroups}
            duplicatesDialogOpen={duplicatesDialogOpen}
            setDuplicatesDialogOpen={setDuplicatesDialogOpen}
            duplicatesLoading={duplicatesLoading}
            handleCheckCatalogDuplicates={handleCheckCatalogDuplicates}
            onMergeGroup={handleMergeGroup}
            selectedCatalogItems={selectedCatalogItems}
            mergeDialogOpen={mergeDialogOpen}
            setMergeDialogOpen={setMergeDialogOpen}
            mergeForm={mergeForm}
            handleOpenMergeDialog={handleOpenMergeDialog}
            handleMergeFormChange={handleMergeFormChange}
            handleMergeCatalogItems={handleMergeCatalogItems}
            duplicateKeys={duplicateKeys}
            openItemId={catalogDrawerItem?.id ?? null}
            onOpenItem={openCatalogItem}
            onBulkSetField={handleBulkSetField}
            onApplySuggestions={handleApplySuggestions}
            canEdit={canEditCatalog}
          />
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <MovementsTab
            movements={movements}
            search={search}
            onSearchChange={setSearch}
            itemDescriptions={itemDescriptions}
            activeFilter={movementTypeFilter}
            onFilterChange={setMovementTypeFilter}
            projects={projects}
            onRecordMovement={canEdit ? openMovementDialog : undefined}
            selectedKey={movementDocKey}
            onSelectDocument={setMovementDocKey}
            onFixItem={canEdit ? fixInCatalog : undefined}
            fixLabelOf={fixLabelOf}
            onPeriodChange={handleMovementPeriod}
            capped={movementsCapped}
            cappedLimit={MOVEMENTS_DEFAULT_LIMIT}
            panelProps={{
              canReverse: canManageStock,
              canLinkPo,
              canAttach: canEdit,
              canManageFiles: canManageStock,
              currentUserId: userKey(user),
              onPrint: printMovementDocument,
              onReversed: fetchData,
              onChanged: fetchData,
              onConfirmReceipt: openReceiveDn,
              canConfirmReceipt: canEdit
            }}
          />
        </TabsContent>
      </Tabs>

      {/* ---------------------------------------------------------------- dialogs & drawers */}
      <AddItemsDialog
        open={catalogDialog}
        onOpenChange={setCatalogDialog}
        existingPartNumbers={inventoryItemOptions.map((item) => item.part_number)}
        onSave={handleSaveCatalogItems}
      />

      <RecordMovementPanel
        open={movementDialog}
        onClose={closeMovementDialog}
        types={MOVEMENT_TYPES}
        form={movementForm}
        setForm={setMovementForm}
        config={selectedMovementConfig}
        onTypeChange={handleMovementTypeChange}
        projects={projects}
        onProjectChange={handleMovementProjectChange}
        boqStatus={movementProjectBoqStatus}
        droppedParts={movementDroppedParts}
        pendingDrop={pendingDrop}
        onConfirmDrop={confirmMovementDrop}
        onKeepProject={keepPreviousMovementProject}
        rows={movementRows}
        onRowChange={handleMovementRowChange}
        onAddRow={handleAddMovementRow}
        onRemoveRow={handleRemoveMovementRow}
        onPasteRows={handlePasteMovementRows}
        catalogOptions={movementCatalogOptions}
        getItemLabel={getMovementItemLabel}
        getRowAvailability={getMovementRowAvailability}
        sourceAvailability={movementSourceAvailability}
        stockNote={movementBookingNote}
        normalizePartNumber={normalizePartNumber}
        onSubmit={handleMovement}
        submitting={movementSubmitting}
        deliveryPreview={deliveryPreview}
        onPrintPreview={printPreviewDn}
        onClosePreview={closeMovementDialog}
        planLabel={convertingPlan ? `From the delivery plan for ${convertingPlan.project_name} · ${formatDate(convertingPlan.planned_date)}` : ''}
      />

      <ReceiveDnSheet
        open={Boolean(receiveDn && receiveDnInfo)}
        onOpenChange={(open) => { if (!open) setReceiveDn(null); }}
        group={receiveDn}
        dnId={receiveDnInfo?.dnId}
        projectName={receiveDnProject?.name || receiveDn?.movements?.[0]?.project_name || ''}
        defaultName={String(receiveDnProject?.project_engineer || '').trim()}
        itemDescriptions={itemDescriptions}
        onReceived={() => { setReceiveDn(null); fetchData(); }}
      />

      <CatalogItemDrawer
        open={Boolean(catalogDrawerItem)}
        onOpenChange={(open) => { if (!open) setCatalogDrawerItem(null); }}
        item={catalogDrawerItem}
        stock={stock}
        sitesBoq={sitesBoq}
        reservations={reservations}
        movements={movements}
        facets={catalogMeta?.facets}
        duplicates={drawerDuplicates}
        canEdit={canEditCatalog}
        canMerge={canDeleteItems}
        onSave={handleSaveCatalogItem}
        onMerge={(items) => handleMergeGroup(items, catalogDrawerItem?.id)}
        onOpenSitesBoq={(partNumber) => { setCatalogDrawerItem(null); navigateTo('sites-boq', { status: 'all', search: partNumber }); }}
        onDirtyChange={handleCatalogDrawerDirty}
        qb={qbStock}
      />

      <DeliveryPlansPanel
        open={plansPanelOpen}
        onOpenChange={setPlansPanelOpen}
        plans={deliveryPlans}
        onNew={() => setPlanEditor({ open: true, plan: null })}
        onEdit={(plan) => setPlanEditor({ open: true, plan })}
        onConvert={handleConvertPlan}
        onCancelPlan={handleCancelPlan}
        canChange={canChangePlan}
        canCreate={canEdit}
      />

      <DeliveryPlanEditor
        open={planEditor.open}
        onOpenChange={(open) => setPlanEditor((current) => ({ ...current, open }))}
        plan={planEditor.plan}
        projects={projects}
        stock={stock}
        onSave={handleSavePlan}
      />

      <Dialog open={reserveDialog} onOpenChange={(open) => {
        setReserveDialog(open);
        if (!open) resetReserveModal();
      }}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading text-lg text-ink">Reserve Stock for Project</DialogTitle></DialogHeader>
          <form onSubmit={handleReserve} className="space-y-3" data-testid="reserve-form">
            <div className="space-y-1">
              <Label className={labelClass}>Project</Label>
              <Select value={reserveForm.project_id} onValueChange={(value) => handleReserveFormChange({ project_id: value, boq_item_id: '' })}>
                <SelectTrigger className={fieldClass} data-testid="reserve-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={labelClass}>Source Location</Label>
              <Select value={reserveForm.location} onValueChange={(value) => handleReserveFormChange({ location: value })}>
                <SelectTrigger className={fieldClass}><SelectValue placeholder="Stock location" /></SelectTrigger>
                <SelectContent>
                  {INVENTORY_LOCATIONS.map((location) => (
                    <SelectItem key={`reserve-location-${location}`} value={location}>{location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={labelClass}>Item</Label>
              <Select value={reserveForm.part_number} onValueChange={(value) => handleReserveFormChange({ part_number: value, boq_item_id: '' })}>
                <SelectTrigger className={fieldClass} data-testid="reserve-part-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {inventoryItemOptions.map(c => <SelectItem key={c.part_number} value={c.part_number}>{getCatalogLabel(c.part_number, reserveForm.location)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {reserveForm.project_id && reserveBoqItems.length > 0 && (
              <div className="space-y-1">
                <Label className={labelClass}>Project BOQ Line</Label>
                <Select
                  value={reserveForm.boq_item_id}
                  onValueChange={(value) => {
                    const item = reserveBoqItems.find((boqItem) => boqItem.id === value);
                    handleReserveFormChange({
                      boq_item_id: value,
                      part_number: item?.part_number || reserveForm.part_number
                    });
                  }}
                >
                  <SelectTrigger className={fieldClass}><SelectValue placeholder="Link project BOQ item" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {reserveProjectBoqOptions.map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.part_number} - {item.description || 'BOQ item'} ({item.boq_qty} {item.unit || 'pcs'})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className={labelClass}>Quantity</Label>
              <Input
                type="number"
                placeholder="Quantity to reserve"
                value={reserveForm.qty_reserved}
                onChange={(e) => handleReserveFormChange({ qty_reserved: e.target.value })}
                required
                className={fieldClass}
                data-testid="reserve-qty-input"
              />
            </div>
            {reserveForm.part_number ? (
              <div className={cn(
                'flex items-start rounded-control px-3 py-2 text-xs',
                reserveHasStockError
                  ? 'bg-status-red-bg text-status-red-ink'
                  : reserveAfterQty < 5 && reserveQty > 0
                    ? 'bg-status-amber-bg text-status-amber-ink'
                    : 'bg-status-green-bg text-status-green-ink'
              )}>
                {reserveHasStockError || (reserveAfterQty < 5 && reserveQty > 0)
                  ? <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                  : <CheckCircle2 className="mr-2 mt-0.5 h-4 w-4 shrink-0" />}
                <span>
                  {reserveHasStockError
                    ? `Requested ${reserveQty} but only ${reserveAvailableQty} available - reduce quantity`
                    : reserveAfterQty < 5 && reserveQty > 0
                      ? `After reserve only ${reserveAfterQty} pcs remaining`
                      : `Available: ${reserveAvailableQty} pcs → After reserve: ${reserveQty > 0 ? reserveAfterQty : reserveAvailableQty} pcs remaining`}
                </span>
              </div>
            ) : null}
            <div className="space-y-1">
              <Label className={labelClass}>Required By Date</Label>
              <Input
                type="date"
                placeholder="Needed by date"
                value={reserveForm.needed_by_date}
                onChange={(e) => handleReserveFormChange({ needed_by_date: e.target.value })}
                className={fieldClass}
                data-testid="reserve-date-input"
              />
            </div>
            {reserveConfirming ? (
              <div className="space-y-3 rounded-xl border border-[color:var(--brand-soft-border)] bg-brand-soft p-3">
                <p className="text-sm text-ink">
                  You are about to reserve {reserveQty} pcs of {reserveSelectedItem?.part_number || reserveForm.part_number} from {reserveForm.location} for project {reserveSelectedProject?.name || 'selected project'}.
                </p>
                <p className="text-xs text-ink-3">
                  Available now: {reserveAvailableQty} → After reserve: {reserveAfterQty}
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button type="button" variant="outline" className="text-ink" onClick={() => setReserveConfirming(false)}>
                    Back
                  </Button>
                  <Button type="submit" data-testid="submit-reserve-btn">
                    Confirm & Reserve
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="submit"
                className="w-full"
                data-testid="submit-reserve-btn"
                disabled={reserveHasStockError}
              >
                Reserve Stock
              </Button>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
